import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getFilterSchema, getAssistantConfig, sendFeedback, getSession, reportDisplayedText } from "./core/service";
import { OTTO_VERSION } from "./version";
import { buildDeck } from "./news/newsGate";
import { hydrateSeen, markSeen, dismissedVersion } from "./news/newsStore";
import HistoryPanel from "./HistoryPanel";
import {
    buildSchemaMaps,
    buildProposal,
    buildApplyPayload,
    buildRemovalPayload,
    buildAvailableFields,
    augmentMapsWithFilterFields,
    warmCatalog,
    resetShownConceptNotes
} from "./capabilities/dashboardFilters/orchestrator";
import { buildTableContext } from "./capabilities/tableView/tableCatalog";
import { isExportChoice, buildTableProposal, describeTableChange } from "./capabilities/tableView/tableProposal";
import { buildPageManifest, shortPageLabel, pageActionLabel, pageReference, pageLabelOf, isKnownPage, describeCapabilities } from "./capabilities/planner/pageManifest";
import {
    captureOrigin,
    pushNavBack,
    peekNavBack,
    popNavBack,
    clearNavBack,
    isCurrentLocation
} from "./capabilities/dashboardNavigation/navBackStore";
import { savePlan, loadPlan, advancePlanCursor, clearPlan, touchPlan, blockPlan } from "./capabilities/planner/pendingPlanStore";
import { buildBrowseResult, narrowOptions } from "./capabilities/dashboardFilters/fieldMention";
import { saveQuestion, loadQuestion, clearQuestion } from "./capabilities/pendingQuestion/pendingQuestionStore";
import { matchAnswer } from "./capabilities/pendingQuestion/answerMatcher";
import { questionFromProposal } from "./capabilities/pendingQuestion/questionFromProposal";
import { resolveRedirectAnswer } from "./capabilities/capabilityRedirect/capabilityRedirect";
import { requiredNavIndices, selectedPlanSteps, ROUTE_HINT_BY_CAPABILITY } from "./capabilities/planner/planProposal";
import {
    suggestionLead,
    clearsLine,
    clearAllLine,
    fieldLabel,
    refineHint,
    clarificationHeadline,
    historyClarifyLine,
    conflictLine,
    blockedPickLine,
    recordsLabel,
    CLARIFY_FALLBACK
} from "./capabilities/dashboardFilters/presenter";
import {
    runFeasibilityCheck,
    baseCascadeFor,
    matchKey,
    isDimensionPick
} from "./capabilities/dashboardFilters/feasibility";
import {
    describeScalar,
    isApplyableScalar,
    isRecordScalarFilter,
    scalarSelectFromMaps,
    selectOptionsOf
} from "./capabilities/dashboardFilters/applyMapping";
import { panelOperatorLabel } from "./core/wildcardOperators";
import { formatNumericValue } from "./core/numberFormat";
import { emitClientSpans, makeSpan } from "./core/telemetry";
import { runStartupDiagnostics, reportFiltersReady } from "./core/startupDiagnostics";
import { loadChat, saveChat, readUserId } from "./core/chatStore";
import {
    RESIZE_HANDLES,
    clampSize,
    defaultSize,
    enlargedSize,
    isEnlarged,
    panelHeight,
    panelWidth,
    resizeFrom
} from "./core/panelSize";
import { clearDecisionLog } from "./core/decisionLog";
import { captureDashboard, fileToScreenshot, imageFromClipboard } from "./core/screenshot";
import "./filterAssistant.css";

// Lazy: the deck's clips are the heaviest assets Otto ships, and nobody should pay for them on a page load
// where the modal never opens. This build already needs a 12GB heap.
const NewsModal = React.lazy(() => import("./news/NewsModal"));

// Client EXECUTION span for a CONFIRMED Otto action, attached to the turn's trace (proposal.requestId).
// The orchestrator emits a `<kind>.ground` span at proposal-build time (what Otto DECIDED); this is its
// partner — what the user then RAN, once. Together they make a capability's whole round-trip visible in the
// tracing waterfall (the filter path already had this via the dashboard's `apply` span; nav/chart/reset/
// table/plan did not). Best-effort and never throws: telemetry must never affect the action or the UI.
// `ids` is the proposal (or any object carrying requestId/sessionId).
const traceExec = (ids, name, { output, status = "ok", metadata } = {}) => {
    try {
        emitClientSpans(ids && ids.requestId, ids && ids.sessionId, [makeSpan(name, { output, status, metadata })]);
    } catch (e) {
        /* best-effort — a telemetry hiccup must not break the action */
    }
};

// Otto's opening message — a welcoming, plain-language intro shown as a bot bubble at the top of the
// chat. Rendered (not stored in `messages`) so it never enters the conversation history sent to the model.
const WELCOME_MESSAGE =
    "👋 Hi there, I’m Otto. Ask me what’s on your mind, and I’ll set the dashboard for you.";

// What the waiting bubble SAYS once the streamed transport reports which route the classifier picked (about
// a second in, well before the turn finishes). Naming the work costs nothing and applies to every route,
// including the ones with no prose to stream — a filter query spends most of its time grounding against the
// project's data, so "Checking your data…" is both truer and more reassuring than a generic spinner. An
// unknown/absent route keeps the generic wording, so a newer backend route can never render a blank label.
export const THINKING_DEFAULT = "Thinking…";
export const THINKING_BY_ROUTE = {
    filter_query: "Checking your data…",
    chart_command: "Looking at your charts…",
    table_command: "Looking at the table…",
    navigate: "Finding that page…",
    platform_qa: "Looking that up in the docs…",
    multi_task: "Planning the steps…",
    dashboard_reset: "Getting ready to reset the dashboard…",
    state_query: "Checking your active filters…"
};
export const thinkingLabel = route => THINKING_BY_ROUTE[route] || THINKING_DEFAULT;

// Pre-baked prompts the user can fire with one tap from the "Click for Examples" modal. Each is sent
// through the exact same submit pipeline as a typed query (interpret → proposal → Apply), so they always
// stay in sync with the live flow — no special-casing. Keep these phrased the way a real user would ask.
// One-tap example prompts grouped by the capability they exercise. Every prompt rides the SAME submit
// pipeline as a typed query and is grounded against live context (filter fields / chart options / reachable
// pages), so an option a chart or page doesn't offer becomes a clarification, never a broken action. The
// modal shows EXAMPLES_STEP per group with a "Load more" that reveals the rest of that same group.
//
// `needs` is the capability a group's prompts actually exercise. Otto mounts on pages with very different
// capability sets, so an unscoped list offers a chart prompt on a page that has no charts — the prompt then
// fires for real and comes back as a clarification, which teaches the user the wrong thing about what Otto
// can do. Groups whose capability is not mounted here are dropped (see `pageExamples`).
const EXAMPLE_CATEGORIES = [
    {
        title: "Ask about your data",
        needs: "filters",
        prompts: [
            "High criticality HVAC over $50k for 2025–2028",
            "Show me the worst FCI buildings",
            "Roofing renewals due this year",
            "Critical priority electrical systems",
            "Plumbing projects between 2026 and 2030"
        ]
    },
    {
        title: "Manage the dashboard",
        needs: "charts",
        prompts: [
            "Change the yearly breakdown chart to a pie chart",
            "Sort the overall cost chart by value",
            "Show only the top 10 in the overall cost chart",
            "Maximize the overall cost chart",
            "Export the overall cost chart as PDF"
        ]
    },
    {
        title: "Navigate the app",
        needs: "navigate",
        prompts: [
            "Take me to the FCA Projects page",
            "Go to Smart Reports & Charts",
            "Open Asset Management",
            "Show me the Recommendations",
            "Go to EFCI Benchmarking"
        ]
    }
];

// How many examples to reveal per group before the "Load more" affordance (and how many each tap adds).
const EXAMPLES_STEP = 3;

// Group key + reveal step for the (single-list) Recommendations modal, so it shares the same Load-more
// machinery as the examples but shows more up front (guidelines are short, so a bigger first page reads well).
const REC_GROUP = "recommendations";
const REC_STEP = 8;

// Guidelines for getting the most out of Otto. Each pairs a short piece of advice (`tip`) with a well-formed
// example (`prompt`) the user can fire with one tap — so the recommendation both teaches good phrasing and
// works when clicked, exactly like the Examples modal. `needs` scopes each to the capability its PROMPT
// exercises, for the same reason the example groups carry one: the tip is only good advice on a page where
// the prompt demonstrating it would actually run.
const RECOMMENDATIONS = [
    { tip: "Be specific — name criticality, cost, and years", prompt: "High criticality HVAC over $50k for 2025–2028", needs: "filters" },
    { tip: "Use the terms from your dashboard (FCI, trade, criticality)", prompt: "Show me the worst FCI buildings", needs: "filters" },
    { tip: "Name the chart you want to change", prompt: "Sort the overall cost chart by value", needs: "charts" },
    { tip: "Ask for one change at a time", prompt: "Group the overall cost chart by trade", needs: "charts" },
    { tip: "Refine step by step — Otto remembers the conversation", prompt: "Only critical priority electrical systems", needs: "filters" },
    { tip: "Sort or export the table by describing what you want", prompt: "Sort the table by FCI, worst first", needs: "table_view" },
    { tip: "Ask Otto to take you to another page by name", prompt: "Take me to the FCA Projects page", needs: "navigate" }
];

// Inline SVGs (no icon dependency in this project) — they inherit `currentColor` so CSS drives the color.
const BotIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <rect x="4.5" y="8" width="15" height="11" rx="3" fill="currentColor" />
        <circle cx="9.5" cy="13.5" r="1.5" fill="#fff" />
        <circle cx="14.5" cy="13.5" r="1.5" fill="#fff" />
        <path d="M12 4.5v3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="3.5" r="1.6" fill="currentColor" />
    </svg>
);

const UserIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.5" fill="currentColor" />
        <path d="M5 19.5c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" fill="currentColor" />
    </svg>
);

const ClearIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path
            d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <path
            d="M6.5 7l.8 11a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9l.8-11"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

// Check badge shown beside a filter once it has been applied to the dashboard — the "active filter"
// marker. Inherits `currentColor`, so the green comes from the applied pill/chip styling around it.
const CheckIcon = () => (
    <svg className="fai-check" viewBox="0 0 24 24" width="12" height="12" fill="none" aria-hidden="true">
        <path
            d="M5 12.5l4.2 4.2L19 6.8"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Counter-clockwise "revert" arrow shown on the Undo button that replaces the "Applied" line once a
// filter is live on the dashboard. Inherits `currentColor`.
const UndoIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
        <path d="M9 5L4 10l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 10h9a6 6 0 0 1 6 6v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Left arrow shown on the "Back to <page>" button that appears once Otto has navigated the user somewhere —
// the navigation twin of UndoIcon. Inherits `currentColor`.
const BackArrowIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
        <path d="M10 5l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 11h15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Small "opens a page" arrow, shown on a Q&A source chip that leads somewhere (the cited page). Inherits
// `currentColor`, so it stays as quiet as the chip it sits in.
const OpenPageIcon = () => (
    <svg className="fai-open-page" viewBox="0 0 24 24" width="11" height="11" fill="none" aria-hidden="true">
        <path d="M8 16L16 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M9.5 8H16v6.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Clock with a counter-clockwise arrow — the "history / past conversations" affordance in the header.
const HistoryIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <path
            d="M3.5 9A9 9 0 1 1 3 12"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M3 5v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// A sparkle for the What's New deck (FTO-128). Deliberately NOT a bell — a bell promises a stream of
// notifications, and this opens one thing: what changed in this release. Deliberately not the gift box it
// started as either: four overlapping stroked paths collapse into a smudge at 16px, where a filled sparkle
// still reads.
const NewsIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <path d="M12 3.2l1.7 4.6 4.6 1.7-4.6 1.7L12 15.8l-1.7-4.6L5.7 9.5l4.6-1.7L12 3.2Z" fill="currentColor" />
        <path d="M18.4 14.6l.85 2.3 2.3.85-2.3.85-.85 2.3-.85-2.3-2.3-.85 2.3-.85.85-2.3Z" fill="currentColor" />
    </svg>
);

// Double-headed diagonal arrow — the "enlarge the window" affordance, deliberately the same glyph the
// nwse resize cursor draws, so the header button and the draggable corners read as the same feature.
const ExpandIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4l6.5 6.5" />
            <path d="M9.5 4H4v5.5" />
            <path d="M20 20l-6.5-6.5" />
            <path d="M14.5 20H20v-5.5" />
        </g>
    </svg>
);

// The same diagonal with both heads turned inward — "restore the window to its normal size". Two arrows,
// not MinimizeIcon's four, so the neighbouring buttons stay tellable apart.
const ShrinkIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4l6.5 6.5" />
            <path d="M5 10.5h5.5V5" />
            <path d="M20 20l-6.5-6.5" />
            <path d="M19 13.5h-5.5V19" />
        </g>
    </svg>
);

// Four arrows collapsing toward the centre — the "minimize" affordance in the header. Collapses the
// panel back to the launcher instead of the old close "×". Inherits `currentColor`.
const MinimizeIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
        <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            {/* top-left arrow, pointing down-right */}
            <path d="M3.5 3.5L9 9" />
            <path d="M9 5.5V9H5.5" />
            {/* top-right arrow, pointing down-left */}
            <path d="M20.5 3.5L15 9" />
            <path d="M18.5 9H15V5.5" />
            {/* bottom-left arrow, pointing up-right */}
            <path d="M3.5 20.5L9 15" />
            <path d="M9 18.5V15H5.5" />
            {/* bottom-right arrow, pointing up-left */}
            <path d="M20.5 20.5L15 15" />
            <path d="M18.5 15H15V18.5" />
        </g>
    </svg>
);

// Paper plane on the round Send button that sits inside the composer. Inherits `currentColor`.
const SendIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
        <path d="M4 11.5L20 4l-7.5 16-2-6.5-6.5-2z" fill="currentColor" />
    </svg>
);

// Lightning bolt on the Actions button beside Send — "what can Otto do on this page". Deliberately a quiet
// outline next to the solid blue plane: it is a reference, not the primary action of the composer.
const ActionsIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
        <path
            d="M13 2.5L5 13.5h5.5L11 21.5l8-11h-5.5L13 2.5z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
        />
    </svg>
);

// Floating modal over the panel with a title + a list of one-tap items. Shared by the Examples and
// Recommendations chips; the caller supplies the body (`children`) so each can lay its items out differently.
const PromptModal = ({ title, onClose, children }) => (
    <div className="fai-examples-overlay" role="dialog" aria-label={title} onClick={onClose}>
        <div className="fai-examples-modal" onClick={e => e.stopPropagation()}>
            <div className="fai-examples-head">
                <span>{title}</span>
                <button type="button" className="fai-examples-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>
            <div className="fai-examples-list">{children}</div>
        </div>
    </div>
);

// DNA double-helix "thinking" loader (two weaving strands), styled in the dashboard blues via CSS.
const DnaLoader = () => (
    <span className="fai-dna" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
            <i key={i} />
        ))}
    </span>
);

// Round avatar badge shown beside each chat bubble. `who` is "assistant" (Otto) or "user".
const Avatar = ({ who }) => (
    <span className={`fai-avatar fai-avatar-${who}`} aria-hidden="true">
        {who === "user" ? <UserIcon /> : <BotIcon />}
    </span>
);

// Wraps a chat bubble in a flex row and places the avatar on the correct side.
const Row = ({ who, children }) => (
    <div className={`fai-row fai-row-${who}`}>
        {who === "assistant" && <Avatar who="assistant" />}
        {children}
        {who === "user" && <Avatar who="user" />}
    </div>
);

const Chip = ({ tone, children }) => <span className={`fai-chip fai-chip-${tone || "neutral"}`}>{children}</span>;

// Short wall-clock label for a chat bubble (e.g. "3:45 PM"). `ts` is an epoch-ms stamp taken when the user
// sends the message. Defensive: an absent/bad stamp (e.g. a conversation persisted before timestamps existed)
// renders nothing rather than "Invalid Date".
const formatTime = ts => {
    if (!ts) return "";
    try {
        return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch (e) {
        return "";
    }
};

const pct = score => `${Math.round((score || 0) * 100)}%`;

const sameMatch = (a, b) => a && b && a.field === b.field && a.name === b.name && a.id === b.id;

// Class for one suggestion pill. Before Apply it toggles the blue "selected" look. After Apply the choice
// is locked in: the picked pill(s) become the green "active filter" state (is-applied), the ones the user
// didn't pick fade back (is-dimmed), so what actually landed on the dashboard reads unmistakably.
// Live-feasibility states: `blocked` = an UNSELECTED pill with zero results under the current numbered
// selections (tapping it explains instead of selecting); `conflicted` = a SELECTED pill whose group would
// empty the dashboard (part of a conflict — Apply is held until it's resolved).
const suggestionClass = (isSelected, applied, { blocked, conflicted } = {}) =>
    `fai-suggestion${isSelected ? (applied ? " is-applied" : " is-selected") : applied ? " is-dimmed" : ""}${
        !isSelected && !applied && blocked ? " is-blocked" : ""
    }${isSelected && !applied && conflicted ? " is-conflict" : ""}`;

// The numbered badge on a selected pill — selection ORDER is meaningful (it drives the feasibility
// check's "which pick broke the combination" attribution), so it's shown, not hidden.
const OrderBadge = ({ order }) => (order != null ? <span className="fai-order">{order}</span> : null);

// Render the assistant's clarification as a STRING. intent.clarification is an OBJECT {question, options};
// returning it raw makes a history turn's `content` a dict, which the backend's ConversationTurn.content
// (typed str) rejects with a 422 — silently breaking the whole multi-turn clarification loop (the user's
// answer to "category or description?" never interprets). Always flatten to "question (opt / opt)".
const clarificationText = clarification => {
    if (!clarification) return "";
    if (typeof clarification === "string") return clarification;
    const question = clarification.question || "";
    const options = (clarification.options || []).join(" / ");
    return options ? `${question} (${options})` : question;
};

// Compact conversation history for multi-turn memory (the backend resolves follow-ups against it).
// MUST return a string for every branch — it becomes ConversationTurn.content.
export const summarizeAssistant = proposal => {
    if (proposal && proposal.chart) return proposal.message || "";
    if (proposal && proposal.navigate) return proposal.message || "";
    if (proposal && proposal.crossCapability) return proposal.message || "";
    if (proposal && proposal.removal) return proposal.message || "";
    // A state answer's headline is only the count ("You have 3 filters applied right now:") — the filters
    // themselves live in the chips, so history carries both or a follow-up ("remove that one") loses them.
    if (proposal && proposal.filterState) return [proposal.message, proposal.summaryText].filter(Boolean).join(" ");
    if (proposal && proposal.smalltalk) return proposal.message || "";
    if (proposal && proposal.clarify) return proposal.message || "";
    // Questions Otto asked with BUTTONS are still turns the next message answers — without these the history
    // recorded "Acknowledged the previous request." in place of the question, so the model resolved a
    // follow-up against a turn that, as far as it could tell, said nothing.
    if (proposal && (proposal.pageChoice || proposal.redirectChoice)) return proposal.message || "";
    // Same for table turns (incl. their clarifications — "Which Region did you mean — CFD, EFD, or WFD?"),
    // plans, and Q&A answers: the backend can only resolve a follow-up against what Otto actually SAID.
    if (proposal && proposal.table) return proposal.message || "";
    if (proposal && proposal.plan) return proposal.message || "";
    if (proposal && proposal.qa) return proposal.qa.assistantMessage || proposal.qa.answer || proposal.message || "";
    const intent = (proposal && proposal.intent) || {};
    if (intent.needs_clarification) {
        const asked = clarificationText(intent.clarification);
        if (asked) return asked;
        const terms = (intent.ambiguous_terms || [])
            .map(a => `"${a.phrase}" (${(a.candidate_dimensions || []).join(" / ")})`)
            .join("; ");
        return terms ? `Asked to clarify ${terms}` : intent.assistant_message || "Asked a clarifying question.";
    }
    return intent.assistant_message || "Acknowledged the previous request.";
};

export const buildHistory = (messages, limit = 6) =>
    messages
        .map(m => {
            if (m.role === "user") return { role: "user", content: m.text };
            if (m.proposal) return { role: "assistant", content: summarizeAssistant(m.proposal) };
            if (m.system) return { role: "assistant", content: m.system };
            return null;
        })
        .filter(Boolean)
        .slice(-limit);

// Rebuild the chat `messages` array from a persisted conversation's turns (GET /sessions/{id}) so the user
// can RESUME it. Each stored turn is one exchange — the user's `utterance` plus the `intent` Otto produced —
// so it expands into TWO messages. Past assistant turns are rendered as read-only Otto bubbles (a `smalltalk`
// proposal): re-instantiating an interactive apply card would need a fresh resolve, out of scope for history.
// Crucially this keeps `buildHistory` producing STRING content for the resumed turns, so multi-turn memory
// stays valid when the user continues typing. Reuses `summarizeAssistant` for the filter-turn summary.
export const rehydrateMessages = (turns = []) =>
    turns.flatMap(t => {
        const intent = t.intent || {};
        // Prefer the turn's DISPLAYED text (what the card actually rendered, reported post-render via
        // /turns/display) — that is exactly what the user saw. Turns from before that landed (or whose
        // report never arrived) fall back: a clarify turn's stored assistant_message is the PRE-resolve
        // model guess the live card suppressed, so those get the deterministic historyClarifyLine instead.
        const displayed = typeof t.displayed_message === "string" ? t.displayed_message.trim() : "";
        const assistantText =
            displayed ||
            (intent.needs_clarification
                ? historyClarifyLine(intent)
                : intent.assistant_message || intent.message || summarizeAssistant({ intent }) || "");
        return [
            // `created_at` (ISO string from the turns table) becomes the user bubble's timestamp so a resumed
            // conversation shows WHEN each message was sent, matching a live one. formatTime parses either an
            // ISO string or an epoch-ms stamp, so no conversion is needed here.
            { role: "user", text: t.utterance || "", ts: t.created_at || null },
            { role: "assistant", proposal: { smalltalk: true, message: assistantText } }
        ];
    });

// The logged-in user's id, read from chatStore (which also keys the persisted chat by it). Otto scopes its
// server calls — interpret, config, and now the history list — to the same id.

// One resolved phrase's pills. Selection is MULTI + ORDERED across the whole card: `orderOf(match)`
// returns the pill's 1-based click order (null = unselected), `countOf(match)` its live record count
// under the OTHER numbered selections (null = unverifiable — never blocked), and `conflictedOf(match)`
// whether the pill belongs to a selected group that would empty the dashboard.
const ResultBlock = ({ result, orderOf, countOf, conflictedOf, onChoose, maps, applied, locked, validating, onOpenFilterWindow, traceIds }) => {
    const { decision, suggestions = [], phrase, chosen, multi, browse } = result;

    // A THRESHOLD result (FTO-12) has no pills of its own: the prompt wildcard pill below carries the
    // editor and the headline carries the ask — rendering nothing here avoids the "no match" chip.
    if (result.threshold) return null;

    const pill = (s, i, label) => {
        const order = orderOf(s);
        const sel = order != null;
        const blocked = !sel && countOf(s) === 0;
        return (
            <button
                key={i}
                type="button"
                className={suggestionClass(sel, applied, { blocked, conflicted: conflictedOf(s) })}
                onClick={() => onChoose(s)}
                // `validating`: a feasibility check is verifying the current numbered selections against
                // the database — every pill freezes until the verdict lands, so the user can't stack
                // further picks on an unverified combination (the card shows a loading overlay).
                disabled={applied || locked || validating}
                title={blocked ? "No results with your current selections" : undefined}
            >
                {applied && sel && <CheckIcon />}
                {!applied && <OrderBadge order={order} />}
                {label}
            </button>
        );
    };

    if (multi) {
        // A BROWSE result (FTO-10): the phrase named this FIELD, so the pills are the field's own options
        // rather than value matches for the word. Same multi mechanics (nothing preselected, toggle +
        // Apply); only the framing and the large-vocabulary escape hatches differ.
        if (browse) {
            const paged = browse.total > browse.shown;
            return (
                <div className="fai-result">
                    <div className="fai-clarify">
                        {browse.narrowWord
                            ? `${browse.label} options matching “${browse.narrowWord}” (tap to toggle):`
                            : paged
                              ? `${browse.label} has ${browse.total} options — here are the first ${browse.shown} (tap to toggle):`
                              : `Your ${browse.label} options (tap to toggle):`}
                    </div>
                    <div className="fai-suggestions">{suggestions.map((s, i) => pill(s, i, s.name))}</div>
                    {paged && (
                        <div className="fai-refine">
                            Type a word and I’ll narrow the list (e.g. “pump”)
                            {onOpenFilterWindow ? ", or see them all in the filter window:" : "."}
                        </div>
                    )}
                    {paged && onOpenFilterWindow && (
                        <div className="fai-chart-actions">
                            <button
                                type="button"
                                className="fai-apply"
                                onClick={() => {
                                    traceExec(traceIds || {}, "field_mention.execute", {
                                        output: { action: "open_filter_window", field: browse.field }
                                    });
                                    onOpenFilterWindow(browse.label);
                                }}
                            >
                                Open the filter window
                            </button>
                        </div>
                    )}
                </div>
            );
        }
        return (
            <div className="fai-result">
                <div className="fai-clarify">All matches for “{phrase}” (tap to toggle):</div>
                <div className="fai-suggestions">{suggestions.map((s, i) => pill(s, i, s.name))}</div>
            </div>
        );
    }

    if (decision === "auto") {
        // High-confidence match: rendered as a tappable pill (not a pre-confirmed chip) so the user makes
        // the selection themselves — nothing is pre-selected. No score shown (Otto is confident here).
        const m = chosen;
        return m ? (
            <div className="fai-result">
                <div className="fai-suggestions">
                    {pill(
                        m,
                        0,
                        <>
                            {fieldLabel(m.field, maps)}: {m.name}
                        </>
                    )}
                </div>
            </div>
        ) : null;
    }
    if (decision === "confirm" || decision === "clarify") {
        return (
            <div className="fai-result">
                <div className="fai-clarify">{suggestionLead(decision, phrase, suggestions.length)}</div>
                <div className="fai-suggestions">
                    {suggestions.map((s, i) =>
                        pill(
                            s,
                            i,
                            <>
                                {fieldLabel(s.field, maps)}: {s.name}
                                {/* A 0% is the embedding calibration FLOOR, not a measurement: anything under
                                    cos 0.30 clamps to zero, so a genuine alternative the resolver deliberately
                                    offered ("1 - Crisis" for "worst") was labelled as certainly-wrong. Options
                                    ride along on a card because the resolver decided they belong there
                                    (FTO-86); printing 0% next to one contradicts the offer. Every real score
                                    still shows. */}
                                {s.score > 0 && <small>({pct(s.score)})</small>}
                            </>
                        )
                    )}
                </div>
                {result.zero_results_note && <div className="fai-zero-note">{result.zero_results_note}</div>}
            </div>
        );
    }
    return <Chip tone="muted">no match for “{phrase}”</Chip>;
};

// Operators offered per wildcard value type, each with a short human label used BOTH for the pill text and
// the inline editor's <select>. The `op` ids match the interpret schema / core/wildcardOperators OPERATOR_MAP
// ("equals" is handled specially there). Numeric + date share the comparison ops; text gets substring ops.
const WILDCARD_OPERATORS = {
    number: [
        { op: "gt", label: ">" },
        { op: "gte", label: "≥" },
        { op: "lt", label: "<" },
        { op: "lte", label: "≤" },
        { op: "equals", label: "=" },
        { op: "between", label: "between" }
    ],
    date: [
        { op: "lt", label: "before" },
        { op: "gt", label: "after" },
        { op: "between", label: "between" },
        { op: "equals", label: "on" }
    ],
    text: [
        { op: "contains", label: "contains" },
        { op: "not_contains", label: "doesn’t contain" },
        { op: "starts_with", label: "starts with" },
        { op: "ends_with", label: "ends with" },
        { op: "equals", label: "is" }
    ]
};

const operatorsForType = t => WILDCARD_OPERATORS[t] || WILDCARD_OPERATORS.text;
const operatorLabel = (op, t) => (operatorsForType(t).find(o => o.op === op) || { label: op }).label;
// The EDITOR names each operator the way the filter window does ("Greater Than", not ">"), so the choice the
// user is making reads the same in both places. The pill keeps the compact symbol — it is a summary, and the
// panel is narrow. Operators the panel has no wording for (every date comparison, a numeric range) keep
// Otto's own word, so nothing ever renders a raw token.
const operatorEditorLabel = (op, t) => panelOperatorLabel(op, t, operatorLabel(op, t));

// A wildcard's value formatted for the pill: numbers get thousands separators, a range shows both bounds,
// text is quoted. Empty when the wildcard carries no value (a bare operator = a "clear this field" pill).
// `fieldInfo` carries the field key + label so core/numberFormat can tell a YEAR from a quantity — without
// it this rendered "Year Built < 1,990".
const formatWildcardValue = (w, valueType, fieldInfo) => {
    const fmtNum = v => formatNumericValue(v, { ...fieldInfo, type: valueType });
    if (w.operator === "between" || w.value_range) {
        const from = (w.value_range && w.value_range.from_value) ?? "";
        const to = (w.value_range && w.value_range.to_value) ?? "";
        const fmt = valueType === "number" ? fmtNum : v => (v == null ? "" : String(v));
        return `${fmt(from)}–${fmt(to)}`;
    }
    if (w.value == null || w.value === "") return "";
    if (valueType === "number") return fmtNum(w.value);
    if (valueType === "text") return `“${w.value}”`;
    return String(w.value);
};

// One numeric / text / date wildcard (e.g. "Area (Sq Ft) > 1,000,000") rendered as an INTERACTIVE pill:
// tap the body to include/exclude it from the Apply, or open the inline editor (operator + value) to change
// it. Starts UNSELECTED like the dimension pills — nothing applies until the user opts in, so a bare
// wildcard can no longer silently satisfy Apply (which used to auto-apply + lock the whole conversation).
// Committing an edit auto-selects it: setting a value is a clear intent to use it.
const WildcardPill = ({ wildcard, maps, selected, applied, locked, validating, onToggle, onEdit }) => {
    const [editing, setEditing] = useState(false);
    const [draftOp, setDraftOp] = useState(wildcard.operator);
    const [draftValue, setDraftValue] = useState(wildcard.value != null ? wildcard.value : "");
    const [draftFrom, setDraftFrom] = useState((wildcard.value_range && wildcard.value_range.from_value) ?? "");
    const [draftTo, setDraftTo] = useState((wildcard.value_range && wildcard.value_range.to_value) ?? "");

    const label = fieldLabel(wildcard.field, maps);
    const meta = maps && maps.wildcardMap && maps.wildcardMap[wildcard.field];
    const valueType = (meta && meta.value_type) || "text";
    const ops = operatorsForType(valueType);
    const disabled = applied || locked || validating;
    // A single-choice OPTION field (the QA-band filters: My QA / QA Passed). There is no operator to pick —
    // it is always "is this one value" — and no free text to type: the editor offers the field's OWN options,
    // so an edit can never produce a value the dashboard doesn't have. The pill shows the option's LABEL
    // ("Pending"), not the API value ("pending").
    const options = selectOptionsOf(meta);
    const isOptionField = options.length > 0;
    const optionLabelOf = v => {
        const hit = options.find(o => String(o.value) === String(v));
        return hit ? hit.label || hit.value : v;
    };

    const openEditor = () => {
        setDraftOp(wildcard.operator);
        setDraftValue(wildcard.value != null ? wildcard.value : "");
        setDraftFrom((wildcard.value_range && wildcard.value_range.from_value) ?? "");
        setDraftTo((wildcard.value_range && wildcard.value_range.to_value) ?? "");
        setEditing(true);
    };
    const commit = () => {
        const isRange = draftOp === "between";
        onEdit({
            operator: draftOp,
            value: isRange ? null : draftValue,
            value_range: isRange ? { from_value: draftFrom, to_value: draftTo } : null
        });
        setEditing(false);
    };

    if (editing && isOptionField) {
        return (
            <div className="fai-wildcard-editor">
                <span className="fai-wildcard-field">{label}</span>
                <span className="fai-wildcard-dash">is</span>
                <select
                    className="fai-wildcard-op"
                    value={draftValue}
                    onChange={e => setDraftValue(e.target.value)}
                    aria-label={`${label} value`}
                >
                    {options.map(o => (
                        <option key={o.value} value={o.value}>
                            {o.label || o.value}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    className="fai-wildcard-done"
                    onClick={() => {
                        onEdit({ operator: "equals", value: draftValue, value_range: null });
                        setEditing(false);
                    }}
                >
                    Done
                </button>
            </div>
        );
    }

    if (editing) {
        const inputType = valueType === "number" ? "number" : valueType === "date" ? "date" : "text";
        return (
            <div className="fai-wildcard-editor">
                <span className="fai-wildcard-field">{label}</span>
                <select className="fai-wildcard-op" value={draftOp} onChange={e => setDraftOp(e.target.value)}>
                    {ops.map(o => (
                        <option key={o.op} value={o.op}>
                            {operatorEditorLabel(o.op, valueType)}
                        </option>
                    ))}
                </select>
                {draftOp === "between" ? (
                    <>
                        <input className="fai-wildcard-input" type={inputType} value={draftFrom} onChange={e => setDraftFrom(e.target.value)} aria-label={`${label} from`} />
                        <span className="fai-wildcard-dash">–</span>
                        <input className="fai-wildcard-input" type={inputType} value={draftTo} onChange={e => setDraftTo(e.target.value)} aria-label={`${label} to`} />
                    </>
                ) : (
                    <input className="fai-wildcard-input" type={inputType} value={draftValue} onChange={e => setDraftValue(e.target.value)} aria-label={`${label} value`} autoFocus />
                )}
                <button type="button" className="fai-wildcard-done" onClick={commit}>
                    Done
                </button>
            </div>
        );
    }

    const valueText = formatWildcardValue(wildcard, valueType, { field: wildcard.field, label });
    // A PROMPT pill (FTO-12 — the user named the field, no value yet): tapping the body opens the editor
    // instead of toggling. Selecting a value-less wildcard would apply as a CLEAR of the field
    // (buildApplyPayload's operator-without-value rule) — the opposite of "filter by cost".
    const isBarePrompt = !!wildcard.prompt && (wildcard.value == null || wildcard.value === "") && !wildcard.value_range;
    return (
        <span className="fai-wildcard">
            <button
                type="button"
                className={suggestionClass(selected, applied)}
                onClick={isBarePrompt ? openEditor : onToggle}
                disabled={disabled}
            >
                {applied && selected && <CheckIcon />}
                {isOptionField ? (
                    `${label}: ${optionLabelOf(wildcard.value)}`
                ) : (
                    <>
                        {label} {operatorLabel(wildcard.operator, valueType)}
                        {valueText ? ` ${valueText}` : isBarePrompt ? " …" : ""}
                    </>
                )}
            </button>
            {!applied && !locked && (
                <button
                    type="button"
                    className="fai-wildcard-edit"
                    onClick={openEditor}
                    disabled={validating}
                    aria-label={`Edit ${label} value`}
                    title="Edit value"
                >
                    ✎
                </button>
            )}
        </span>
    );
};

// A year span as pill text: a single year stands alone, an open end shows an ellipsis.
const yearSpanLabel = y => {
    if (!y) return "";
    const s = y.start_year != null ? y.start_year : null;
    const e = y.end_year != null ? y.end_year : null;
    if (s != null && e != null) return s === e ? `${s}` : `${s}–${e}`;
    return `${s != null ? s : "…"}–${e != null ? e : "…"}`;
};

// The capital-plan year span Otto read out of the request ("due this year" -> 2026), as an INTERACTIVE pill.
// It used to be a static chip that was always on: it alone could satisfy Apply, so a year Otto inferred —
// and inferring is exactly what it does here, since the user rarely types a literal year — applied itself
// with no way to correct it. That is the same defect the wildcard pills had, so this is the same fix: starts
// UNSELECTED, tap to include it, ✎ to change it, and committing an edit selects it. The editor offers the
// client's REAL plan years (`available.years`) rather than a free-text box, so a correction can't invent a
// year the project doesn't have; without that list (grounding couldn't reach the options API) it degrades to
// plain number inputs.
const YearPill = ({ year, available, selected, applied, locked, validating, onToggle, onEdit }) => {
    const [editing, setEditing] = useState(false);
    const [draftStart, setDraftStart] = useState(year.start_year != null ? year.start_year : "");
    const [draftEnd, setDraftEnd] = useState(year.end_year != null ? year.end_year : "");

    const years = (available && available.years) || null;
    const disabled = applied || locked || validating;

    const openEditor = () => {
        setDraftStart(year.start_year != null ? year.start_year : "");
        setDraftEnd(year.end_year != null ? year.end_year : "");
        setEditing(true);
    };
    const commit = () => {
        const toYear = v => {
            const n = parseInt(v, 10);
            return Number.isFinite(n) ? n : null;
        };
        const start = toYear(draftStart);
        const end = toYear(draftEnd);
        // A backwards range ("2028 to 2026") is a slip, not an intent — read it in the order that works.
        const flipped = start != null && end != null && start > end;
        onEdit({ start_year: flipped ? end : start, end_year: flipped ? start : end });
        setEditing(false);
    };

    if (editing) {
        const bound = (value, onChange, label) =>
            years ? (
                <select className="fai-wildcard-op" value={value} onChange={e => onChange(e.target.value)} aria-label={label}>
                    {years.map(y => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </select>
            ) : (
                <input className="fai-wildcard-input" type="number" value={value} onChange={e => onChange(e.target.value)} aria-label={label} />
            );
        return (
            <div className="fai-wildcard-editor">
                <span className="fai-wildcard-field">Years</span>
                {bound(draftStart, setDraftStart, "Years from")}
                <span className="fai-wildcard-dash">–</span>
                {bound(draftEnd, setDraftEnd, "Years to")}
                <button type="button" className="fai-wildcard-done" onClick={commit}>
                    Done
                </button>
            </div>
        );
    }

    return (
        <span className="fai-wildcard">
            <button type="button" className={suggestionClass(selected, applied)} onClick={onToggle} disabled={disabled}>
                {applied && selected && <CheckIcon />}
                years: {yearSpanLabel(year)}
            </button>
            {!applied && !locked && (
                <button type="button" className="fai-wildcard-edit" onClick={openEditor} disabled={validating} aria-label="Edit years" title="Edit years">
                    ✎
                </button>
            )}
        </span>
    );
};

// Thumbs up/down on any Otto reply, tied to the turn via request_id. Shared by the filter proposal and
// the chart proposal so both feed the same 👍/👎 signal (eval + tuning). Fire-and-forget.
// Clicking a rating opens a small compose step where the user can add a note AND/OR attach a screenshot
// (one-click capture of the dashboard, or upload/paste an image) — human-review context for the tracing
// dashboard. The screenshot is best-effort: a capture failure just sends the feedback without one.
export const FeedbackBar = ({ requestId, utterance }) => {
    const [fbState, setFbState] = useState("idle"); // idle | composing | done
    const [rating, setRating] = useState(null);
    const [note, setNote] = useState("");
    const [screenshot, setScreenshot] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const fileInputRef = useRef(null);
    const noteRef = useRef(null);

    // Grow the note textarea to fit its content so a long note stays fully visible instead of being
    // clipped to the initial 2-row box (the field is resize:none, so the user can't enlarge it manually).
    const autosizeNote = useCallback(() => {
        const el = noteRef.current;
        if (!el) return;
        el.style.height = "auto"; // reset first so the box can also SHRINK back when text is deleted
        el.style.height = `${el.scrollHeight}px`;
    }, []);

    // Size to fit when the compose step opens (covers autoFocus and any pre-filled note).
    useLayoutEffect(() => {
        if (fbState === "composing") autosizeNote();
    }, [fbState, autosizeNote]);

    const begin = r => {
        setRating(r);
        setFbState("composing");
    };

    const attachFile = async file => {
        if (!file) return;
        try {
            setScreenshot(await fileToScreenshot(file));
        } catch (e) {
            /* best-effort: leave the current screenshot / none */
        }
    };

    const capture = async () => {
        setCapturing(true);
        try {
            const shot = await captureDashboard();
            if (shot) setScreenshot(shot);
        } finally {
            setCapturing(false);
        }
    };

    const onPaste = e => {
        const file = imageFromClipboard(e);
        if (file) {
            e.preventDefault();
            attachFile(file);
        }
    };

    const send = () => {
        if (!requestId || !rating) return;
        setFbState("done");
        sendFeedback({
            request_id: requestId,
            rating,
            note: note && note.trim() ? note.trim() : undefined,
            utterance,
            screenshot: screenshot || undefined
        }).catch(() => {});
    };

    if (!requestId) return null;
    return (
        <div className="fai-feedback">
            {fbState === "done" && <span className="fai-feedback-thanks">Thanks for the feedback!</span>}
            {fbState === "idle" && (
                <>
                    <span className="fai-feedback-q">Was this helpful?</span>
                    <button type="button" className="fai-feedback-btn" aria-label="Helpful" onClick={() => begin("up")}>
                        👍
                    </button>
                    <button type="button" className="fai-feedback-btn" aria-label="Not helpful" onClick={() => begin("down")}>
                        👎
                    </button>
                </>
            )}
            {fbState === "composing" && (
                <div className="fai-feedback-note">
                    <span className="fai-feedback-q">
                        {rating === "up" ? "Glad it helped! " : "What did you expect? "}Add a note or screenshot (optional).
                    </span>
                    <textarea
                        ref={noteRef}
                        className="fai-feedback-input"
                        value={note}
                        placeholder={rating === "up" ? "Anything worth noting?" : "e.g. “make it a pie” should target the overall cost chart"}
                        rows={2}
                        maxLength={2000}
                        autoFocus
                        onChange={e => {
                            setNote(e.target.value);
                            autosizeNote();
                        }}
                        onPaste={onPaste}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                    />
                    <div className="fai-feedback-attach">
                        <button type="button" className="fai-feedback-attach-btn" onClick={capture} disabled={capturing}>
                            {capturing ? "Capturing…" : "📷 Capture view"}
                        </button>
                        <button
                            type="button"
                            className="fai-feedback-attach-btn"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        >
                            📎 Upload
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={e => attachFile(e.target.files && e.target.files[0])}
                        />
                        {screenshot && (
                            <span className="fai-feedback-preview">
                                <img className="fai-feedback-thumb" src={screenshot} alt="attached screenshot" />
                                <button
                                    type="button"
                                    className="fai-feedback-remove"
                                    aria-label="Remove screenshot"
                                    onClick={() => setScreenshot(null)}
                                >
                                    ✕
                                </button>
                            </span>
                        )}
                    </div>
                    <button type="button" className="fai-feedback-send" onClick={send}>
                        Send
                    </button>
                </div>
            )}
        </div>
    );
};

// Applied state is lifted to FilterAssistant, which tracks a STACK of applied cards (filters accumulate
// across questions — no card is frozen): `applied` = THIS card's filter is live on the dashboard; `undoable`
// = it's the most recent apply, so it shows an interactive Undo (an earlier applied card shows a static
// "Applied", since the snapshot stack only unwinds newest-first). Undo reverts this card's apply (onUndo) and
// returns the card to its fresh, selectable proposal state. `locked` is retained (always false in the app
// now) purely so the component still honors a frozen state when rendered in isolation / tests.
export const AssistantProposal = ({ proposal, maps, applied, locked, undoable = true, onApply, onUndo, feasibilityCtx, autoRun = false, onOpenFilterWindow }) => {
    const { intent, resolution, yearAdjustment } = proposal;

    // The card opens with NOTHING selected. Otto no longer pre-picks anything — not the top-scored guess
    // (confirm), the confident match (auto), or every match (multi) — because a pre-selected pill was
    // confusing: users couldn't tell what they'd chosen vs. what Otto assumed. The user now builds their
    // own selection by tapping pills. Selection order is still first-class (each pick gets a visible
    // number, and the feasibility check uses that order to say WHICH pick made a combination impossible).
    // Reused to RESET the card back to an empty selection on Undo.
    const initialOrdered = useCallback(() => [], []);

    const [ordered, setOrdered] = useState(initialOrdered);

    // Interpreted wildcard filters (numeric/text/date, e.g. "Area (Sq Ft) > 1,000,000") as an EDITABLE local
    // copy. Each starts UNSELECTED — like the dimension pills, nothing is applied until the user opts in, so a
    // wildcard the model volunteered can no longer auto-satisfy Apply (the old always-on chip silently applied
    // itself and locked the conversation). The user toggles each pill on/off and can inline-edit its
    // operator/value; only the selected ones go into the Apply payload. Reused to RESET on Undo.
    const initialWildcards = useCallback(
        () => [
            ...(intent.wildcard_filters || []).map(w => ({
                field: w.field,
                operator: w.operator,
                value: w.value,
                value_range: w.value_range || null,
                prompt: !!w.prompt, // FTO-12 threshold prompt — see WildcardPill's isBarePrompt
                selected: false
            })),
            // A QA-band scalar ("show me my pending QA issues" -> My QA: Pending) is a RECORD-level filter, so
            // it belongs with the wildcard pills rather than among the view-toggle chips: opt-in like every
            // other pick (an auto-checked filter nobody asked to check is the exact defect the wildcard pills
            // were built to kill), editable to another option, and — because a selected pill is a real
            // constraint — counted, so the card can say "0 Records" instead of applying into an empty view.
            ...(intent.scalar_filters || [])
                .filter(s => isRecordScalarFilter(s, maps))
                .map(s => ({
                    field: s.key,
                    operator: "equals",
                    value: scalarSelectFromMaps(s.key, s.value, maps).option.value,
                    value_range: null,
                    prompt: false,
                    selected: false
                }))
        ],
        [intent, maps]
    );
    const [wildcards, setWildcards] = useState(initialWildcards);
    const toggleWildcard = i => setWildcards(prev => prev.map((w, j) => (j === i ? { ...w, selected: !w.selected } : w)));
    const editWildcard = (i, patch) => setWildcards(prev => prev.map((w, j) => (j === i ? { ...w, ...patch, selected: true } : w)));
    const selectedWildcards = wildcards.filter(w => w.selected);

    // The year span, held as the same kind of editable, initially-UNSELECTED local state as the wildcards
    // (see YearPill for why it stopped being a static always-on chip). `groundYearRange` has already resolved
    // it against the clock and the client's real plan years; a grounding that DROPPED the span (out of the
    // plan's range) leaves no pill at all — its note explains why.
    const grounded = yearAdjustment
        ? yearAdjustment.dropped
            ? null
            : yearAdjustment.appliedRange
        : intent.year_range && (intent.year_range.start_year != null || intent.year_range.end_year != null)
        ? intent.year_range
        : null;
    const initialYear = useCallback(
        () => (grounded ? { start_year: grounded.start_year, end_year: grounded.end_year, selected: false } : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [intent, yearAdjustment]
    );
    const [year, setYear] = useState(initialYear);
    const toggleYear = () => setYear(prev => (prev ? { ...prev, selected: !prev.selected } : prev));
    const editYear = patch => setYear(prev => (prev ? { ...prev, ...patch, selected: true } : prev));
    // Only a year the user actually turned on reaches the dashboard, the feasibility counts, and Apply.
    const selectedYear = year && year.selected ? { start_year: year.start_year, end_year: year.end_year } : null;
    // Live feasibility of the CURRENT numbered selections (order-aware, against real data):
    // `counts` = per-pill record count under the other selections, `conflicts` = selected groups that
    // would empty the dashboard, `viableByField` = what IS possible per field. `checking` holds Apply.
    const [feasibility, setFeasibility] = useState(null);
    const [checking, setChecking] = useState(false);
    // One-shot explanation shown when the user taps a pill that would empty the dashboard (we don't
    // select it — we say why not, and what would fit). Cleared on the next successful interaction.
    const [blockedNotice, setBlockedNotice] = useState(null);

    const countFor = useCallback(
        m => {
            if (!feasibility || !feasibility.counts) return null;
            const c = feasibility.counts[matchKey(m)];
            return c === undefined ? null : c;
        },
        [feasibility]
    );
    const conflicts = (feasibility && feasibility.conflicts) || [];

    const orderIndexOf = (ref, match) => {
        const i = ordered.findIndex(s => s.ref === ref && sameMatch(s.match, match));
        return i === -1 ? null : i + 1;
    };
    const conflictedOf = match => conflicts.some(c => c.picks.some(p => sameMatch(p.match, match)));

    // Toggle a pill in the ordered selection. Adding a pill the live check says has ZERO results under
    // the current numbered picks is refused with an explanation (blockedNotice) — "tell the user what is
    // possible" instead of letting them build a combination that can't show data. Unverifiable pills
    // (text search, uncovered fields, no check yet) always toggle freely.
    const choose = (ref, match) => {
        const existing = ordered.findIndex(s => s.ref === ref && sameMatch(s.match, match));
        if (existing !== -1) {
            setOrdered(prev => prev.filter((_, i) => i !== existing));
            setBlockedNotice(null);
            return;
        }
        if (countFor(match) === 0) {
            const viable = (feasibility && feasibility.viableByField && feasibility.viableByField[match.field]) || [];
            setBlockedNotice(blockedPickLine(match, viable, maps));
            return;
        }
        setOrdered(prev => [...prev, { ref, match }]);
        setBlockedNotice(null);
    };

    // Run the order-aware feasibility check whenever the numbered selections (or the dashboard's own
    // filter state) change. Debounced so a burst of taps costs one check; token-guarded so only the
    // LATEST result lands. Best-effort by contract: a failed check clears to "unverifiable" (everything
    // selectable, Apply enabled) — infrastructure must never block the user (same rule as the cascaded-
    // count guard). Skipped entirely once applied/locked (selections are frozen) and when no dimension
    // pick is selected (nothing to verify).
    const scope = feasibilityCtx && feasibilityCtx.scope;
    const activeFilters = feasibilityCtx && feasibilityCtx.activeFilters;
    // Stable string keys: activeFilters is rebuilt by the dashboard every render, so depending on the
    // object identity would re-fire the effect (and the network call) on every parent render.
    const orderedKey = JSON.stringify(ordered.map(s => [s.ref, s.match.field, s.match.id, s.match.name]));
    // The year narrows the same cascade the pills are counted against, so toggling or editing it has to
    // re-run the check — otherwise the counts describe a year the user isn't actually asking for.
    const yearKey = JSON.stringify(selectedYear);
    // Same for the wildcard pills (cost thresholds etc.): toggling or inline-editing one changes the
    // modeled conditioning state, so the counts must follow.
    const wildcardsKey = JSON.stringify(selectedWildcards.map(w => [w.field, w.operator, w.value, w.value_range]));
    const cascadeKey = JSON.stringify(baseCascadeFor(activeFilters, intent, maps || {}));
    const scopeKey = JSON.stringify(scope || null);
    // The raw dashboard params condition the dynamic-field /get_list counts, and can change without the
    // cascade changing (e.g. an applied wildcard) — key the effect on them too.
    const rawKey = JSON.stringify((activeFilters && activeFilters.raw) || null);
    useEffect(() => {
        if (!feasibilityCtx || applied || locked) return undefined;
        // A selected YEAR or WILDCARD (cost threshold etc.) is a real constraint on its own: it must
        // produce a count ("N Records" under that constraint) and a greying basis for the offered pills,
        // even before any dimension pick is selected. Without this, a year-only selection ran no check
        // at all — the year looked like it "didn't affect the count" (same for cost).
        if (!ordered.some(s => isDimensionPick(s.match)) && !selectedYear && !selectedWildcards.length) {
            setFeasibility(null);
            setChecking(false);
            return undefined;
        }
        let stale = false;
        setChecking(true);
        const timer = setTimeout(async () => {
            try {
                const allMatches = [];
                (resolution.results || []).forEach(r => (r.suggestions || []).forEach(s => allMatches.push(s)));
                const result = await runFeasibilityCheck({
                    scope,
                    baseCascade: baseCascadeFor(activeFilters, intent, maps || {}),
                    year: selectedYear,
                    ordered,
                    intent,
                    maps: maps || {},
                    allMatches,
                    // The dashboard's RAW applied params — what dynamic (inline-option) fields are counted
                    // against via the dashboard's own /get_list (the catalog cascade can't count them).
                    rawParams: activeFilters && activeFilters.raw,
                    // The SELECTED wildcard pills (cost thresholds etc.), neutral apply shape — modeled
                    // into the /get_list conditioning so the count honors them; filterFields translate
                    // them exactly as Apply would (applyMapping).
                    wildcards: selectedWildcards.map(w => ({
                        field: w.field,
                        operator: w.operator,
                        value: w.value,
                        value_range: w.value_range
                    })),
                    filterFields: feasibilityCtx.filterFields
                });
                if (!stale) setFeasibility(result);
            } catch (e) {
                if (!stale) setFeasibility(null); // degrade: unverifiable, never block
            } finally {
                if (!stale) setChecking(false);
            }
        }, 250);
        return () => {
            stale = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderedKey, cascadeKey, scopeKey, rawKey, yearKey, wildcardsKey, applied, locked, feasibilityCtx ? 1 : 0]);

    const hasYear = !!year; // a year pill is offered
    const hasSelectedYear = !!selectedYear; // ...and the user turned it on
    // Only SELECTED wildcards count toward Apply — an unselected wildcard must not enable Apply (that
    // silent enable is what let a volunteered "over X SF" filter apply itself and lock the conversation).
    const hasSelectedWildcards = selectedWildcards.length > 0;
    // Selected WILDCARDS are now countable: the feasibility total is recovered via the dashboard's own
    // /get_list with the wildcard modeled into the conditioning params (a wildcard the modeling can't
    // express yields a null total, which recordsLabel hides — never an overstated number). Free-text
    // "contains" picks remain uncountable, so they still hide the label (grounding principle — never
    // show a number we can't stand behind).
    const recordCountAccurate = !ordered.some(s => !isDimensionPick(s.match));
    const scalarFilters = intent.scalar_filters || [];
    // Scalars split three ways. A dashboard VIEW toggle (COST / STATIC FCI / Dashboard Source) is a chip that
    // follows the request — it changes how the dashboard presents data, there is nothing to count or opt into.
    // A RECORD-level filter (the QA band) was moved into the wildcard pills above, so it is not a chip at all.
    // Anything left maps to nothing on this page and keeps an honest note instead of a pill that does nothing.
    const supportedScalars = scalarFilters.filter(s => isApplyableScalar(s, maps) && !isRecordScalarFilter(s, maps));
    const unsupportedScalars = scalarFilters.filter(s => s && s.key && !isApplyableScalar(s, maps));
    const expectedResolutions = (intent.common_filters || []).length + (intent.ambiguous_terms || []).length;
    const resolveFailed = expectedResolutions > 0 && (resolution.results || []).length === 0;
    // Otto made a guess or asked (not a confident auto/apply) -> offer a gentle "refine" nudge.
    const needsRefinement = (resolution.results || []).some(r => r.decision === "confirm" || r.decision === "clarify");
    const canApply =
        !!onApply &&
        (ordered.length > 0 ||
            hasSelectedYear ||
            hasSelectedWildcards ||
            supportedScalars.length > 0 ||
            intent.clear_all ||
            (intent.clears || []).length > 0);
    // The order-aware pre-apply gate: a combination the live check says can't show data is NOT
    // applyable, and while a check is in flight Apply holds (a stale "possible" must not slip through).
    const applyHeld = conflicts.length > 0 || checking;
    // While the check runs, the WHOLE selection area freezes under a loading overlay (not just Apply):
    // a tap during validation would stack an unverified pick onto a combination we're still confirming
    // against the database. Applied/locked cards are already frozen, so no overlay there.
    const validating = checking && !applied && !locked;

    // buildApplyPayload's contract is selections keyed by result ref (it already normalizes arrays), so
    // the ordered list folds back into { ref: [match, ...] } in click order.
    const selectionsForPayload = () => {
        const byRef = {};
        ordered.forEach(({ ref, match }) => {
            (byRef[ref] = byRef[ref] || []).push(match);
        });
        return byRef;
    };

    const handleApply = () => {
        if (applyHeld) return;
        // Pass the trace handle alongside the payload so the apply step (the last, untraced mile where a
        // dropped paramKey silently no-ops) records an `apply` span onto THIS message's trace. The parent
        // pushes this card onto its applied stack (marking it applied + the newest, undoable one).
        onApply(
            buildApplyPayload({
                intent,
                resolution,
                selections: selectionsForPayload(),
                maps,
                yearAdjustment,
                // Only the pills the user actually toggled on (with any inline edits) — strip the `selected`
                // flag back to the neutral wildcard shape buildApplyPayload expects.
                wildcards: selectedWildcards.map(w => ({
                    field: w.field,
                    operator: w.operator,
                    value: w.value,
                    value_range: w.value_range
                })),
                // Same contract for the year pill: the user's toggled-on (and possibly edited) span, or null
                // when they left it off. Passing it explicitly is what stops a year Otto merely inferred
                // from riding along on an apply the user meant for the dimension pills.
                year: selectedYear
            }),
            {
                requestId: proposal.requestId,
                sessionId: proposal.sessionId
            }
        );
    };

    // PLAN MODE (autoRun): this card is a step of an already-CONFIRMED plan, so a FULLY-CONFIDENT
    // resolution applies itself — Otto "taps the pills" (each result's auto-chosen match) and applies
    // through the SAME live feasibility gate a human tap goes through. Anything less than fully-auto
    // (a clarify/confirm/group result) leaves the card interactive: the plan PAUSES, the user picks and
    // applies, and the plan resumes on apply (grounding never guesses — locked rule).
    const autoRunTriedRef = useRef(false);
    const [autoApplyPending, setAutoApplyPending] = useState(false);
    useEffect(() => {
        if (!autoRun || autoRunTriedRef.current || applied || locked) return;
        autoRunTriedRef.current = true;
        const results = resolution.results || [];
        const fullyAuto = results.every(r => r.decision === "auto" && r.chosen && !r.multi);
        if (!fullyAuto && results.length > 0) return; // genuine ambiguity -> pause for the user
        if (results.length > 0) setOrdered(results.map(r => ({ ref: r.ref, match: r.chosen })));
        // A confirmed plan step applies its wildcards and its year too — select them (the interactive default
        // is unselected, which would otherwise silently drop a plan's "over X SF" bound or its year span).
        if (wildcards.length) setWildcards(prev => prev.map(w => ({ ...w, selected: true })));
        setYear(prev => (prev ? { ...prev, selected: true } : prev));
        setAutoApplyPending(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRun, applied, locked]);
    const sawFeasibilityRef = useRef(false);
    useEffect(() => {
        if (!autoApplyPending) return;
        if (checking) {
            sawFeasibilityRef.current = true; // the live check engaged — wait for its result
            return;
        }
        // With dimension picks + a feasibility context, the check WILL start (its effect fires on the
        // ordered change in this same commit, but our `checking` closure is one render stale) — so don't
        // apply until we've SEEN it run and finish. Wildcard/year/scalar-only payloads have no check.
        const hasDimPicks = ordered.some(s => isDimensionPick(s.match));
        if (feasibilityCtx && hasDimPicks && !sawFeasibilityRef.current) return;
        setAutoApplyPending(false);
        if (conflicts.length > 0 || !canApply || applyHeld) return; // blocked/empty -> leave the card for the user
        handleApply();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoApplyPending, checking, conflicts.length, canApply, applyHeld, ordered]);

    // Undo: revert the dashboard filter this card applied (onUndo restores the pre-apply params) and reset
    // the card back to its fresh proposal state so the conversation unlocks and the user can pick again.
    const handleUndo = () => {
        if (onUndo) onUndo();
        setOrdered(initialOrdered());
        setWildcards(initialWildcards());
        setYear(initialYear());
        setBlockedNotice(null);
    };

    // The top line. When Otto is ASKING (needs_clarification), the model's own assistant_message was written
    // BEFORE the resolver ran and often names fields we don't actually offer (free-text description/notes/
    // search are dropped as clarify options). So for a clarification we speak from the RESOLVED options
    // instead — words that match the buttons below. For a committed/auto summary the model's message is a
    // fine grounded recap (the specifics also render as chips), so we keep it.
    const sayText = intent.needs_clarification
        ? clarificationHeadline(resolution.results, maps) || CLARIFY_FALLBACK
        : intent.assistant_message;

    // Report what this card actually RENDERED, once, so a history resume replays what the user SAW
    // (POST /turns/display) — the persisted intent only carries the model's pre-resolve draft, which this
    // card often suppresses. Composed from the same pieces the JSX shows: top line, each result's lead +
    // offered pills, the concept note, the refine hint. Best-effort fire-and-forget; guarded so a missing
    // service stub (isolated renders/tests) can never break the card.
    const displayReportedRef = useRef(false);
    useEffect(() => {
        if (displayReportedRef.current || !proposal.requestId) return;
        displayReportedRef.current = true;
        const lines = [sayText];
        (resolution.results || []).forEach(r => {
            if (r.decision === "auto" && r.chosen) {
                lines.push(`${fieldLabel(r.chosen.field, maps)}: ${r.chosen.name}`);
                return;
            }
            const sugg = r.suggestions || [];
            if (!sugg.length) return;
            lines.push(
                r.browse
                    ? `${r.browse.label} options (${r.browse.shown} of ${r.browse.total}):`
                    : r.multi
                      ? `All matches for “${r.phrase}” (tap to toggle):`
                      : suggestionLead(r.decision, r.phrase, sugg.length),
                sugg.map(s => `${fieldLabel(s.field, maps)}: ${s.name}`).join(", ")
            );
        });
        if (proposal.conceptNote) lines.push(proposal.conceptNote);
        if (needsRefinement) lines.push(refineHint());
        try {
            if (typeof reportDisplayedText === "function") {
                const p = reportDisplayedText({ requestId: proposal.requestId, message: lines.filter(Boolean).join("\n") });
                if (p && typeof p.catch === "function") p.catch(() => {});
            }
        } catch (e) {
            /* best-effort side channel — never break the card */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fai-msg fai-assistant">
            {sayText && <div className="fai-say">{sayText}</div>}
            <div className={`fai-chips${validating ? " is-validating" : ""}`}>
                {validating && (
                    <div className="fai-validating-overlay" role="status" aria-label="Checking filter availability">
                        <DnaLoader />
                        <span className="fai-validating-text">Checking what’s possible…</span>
                    </div>
                )}
                {(resolution.results || []).map((r, i) => (
                    <ResultBlock
                        key={i}
                        result={r}
                        orderOf={m => orderIndexOf(r.ref, m)}
                        countOf={countFor}
                        conflictedOf={conflictedOf}
                        maps={maps}
                        applied={applied}
                        locked={locked}
                        validating={validating}
                        onChoose={m => choose(r.ref, m)}
                        onOpenFilterWindow={onOpenFilterWindow}
                        traceIds={{ requestId: proposal.requestId, sessionId: proposal.sessionId }}
                    />
                ))}
                {hasYear && (
                    <div className="fai-result">
                        <div className="fai-suggestions">
                            <YearPill
                                year={year}
                                available={yearAdjustment && yearAdjustment.available}
                                selected={year.selected}
                                applied={applied}
                                locked={locked}
                                validating={validating}
                                onToggle={toggleYear}
                                onEdit={editYear}
                            />
                        </div>
                    </div>
                )}
                {supportedScalars.map((s, i) => (
                    <Chip key={`s${i}`} tone="ok">
                        {applied && <CheckIcon />}
                        {describeScalar(s, maps)}
                    </Chip>
                ))}
                {unsupportedScalars.map((s, i) => (
                    <Chip key={`us${i}`} tone="neutral">
                        {describeScalar(s, maps)}
                    </Chip>
                ))}
                {wildcards.length > 0 && (
                    <div className="fai-result">
                        <div className="fai-suggestions">
                            {wildcards.map((w, i) => (
                                <WildcardPill
                                    key={i}
                                    wildcard={w}
                                    maps={maps}
                                    selected={w.selected}
                                    applied={applied}
                                    locked={locked}
                                    validating={validating}
                                    onToggle={() => toggleWildcard(i)}
                                    onEdit={patch => editWildcard(i, patch)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {/* Order-aware feasibility verdicts: which numbered pick breaks the combination + what IS
                possible instead. blockedNotice is the same message for a tap we refused. */}
            {!applied &&
                conflicts.map((c, i) => (
                    <div key={`cf${i}`} className="fai-conflict-note">
                        {conflictLine(c, maps)}
                    </div>
                ))}
            {!applied && blockedNotice && <div className="fai-conflict-note">{blockedNotice}</div>}
            {needsRefinement && <div className="fai-refine">{refineHint()}</div>}
            {intent.clear_all ? (
                <div className="fai-clear-note">{clearAllLine()}</div>
            ) : (
                (intent.clears || []).length > 0 && <div className="fai-clear-note">{clearsLine(intent.clears, maps)}</div>
            )}
            {resolveFailed && (
                <div className="fai-error">
                    Couldn’t load filter options for your request (no resolve result). Check the browser console and that
                    the filter-ai service + the options API are reachable.
                </div>
            )}
            {yearAdjustment && yearAdjustment.note && <div className="fai-year-note">{yearAdjustment.note}</div>}
            {/* One-line KB-grounded definition of a concept this turn touches (e.g. what FCI means) —
                a quiet aside, deduped per session by the orchestrator (pickConceptNote). */}
            {proposal.conceptNote && <div className="fai-concept-note">{proposal.conceptNote}</div>}
            {/* What's left is a scalar Otto understood but cannot SET here — either the field isn't on this page
                at all (a recommendations-table-only toggle) or the form it names isn't one Otto can ground
                ("surveyor: me" — Surveyor IS a dashboard filter, but resolving "me" to a surveyor is its own
                piece of work). So the note claims only what's true: it was left out. It never says "coming
                next" (a promise) and never says the field doesn't exist (often false). */}
            {unsupportedScalars.length > 0 && (
                <div className="fai-preview-note">
                    I couldn’t set {unsupportedScalars.map(s => describeScalar(s, maps)).join(", ")} from here, so I left
                    {unsupportedScalars.length > 1 ? " those" : " that"} out.
                </div>
            )}
            {/* Apply button + the live record count, on ONE row with the count to the RIGHT of the button
                (a gap separates them). The count = how many records the dashboard would show if these picks
                were applied (feasibility cascade total). Shown once a dimension pick is made; while a re-check
                runs it dims but keeps the last number. Hidden when it can't be verified (nothing selected, an
                uncountable wildcard/text pick is selected, or an older backend that doesn't report it). */}
            <div className="fai-apply-row">
                {onApply ? (
                    applied ? (
                        // The applied filter is live on the dashboard. Only the MOST RECENT apply is undoable
                        // (the dashboard's snapshot stack unwinds newest-first), so an older applied card shows a
                        // static "Applied" marker instead of an Undo that couldn't cleanly revert out of order.
                        undoable ? (
                            <button type="button" className="fai-undo" onClick={handleUndo} title="Revert this filter">
                                <UndoIcon /> Undo
                            </button>
                        ) : (
                            <div className="fai-applied-static">
                                <CheckIcon /> Applied
                            </div>
                        )
                    ) : (
                        // `applyHeld`: the numbered selections can't show data (conflict) or are still being
                        // verified — never apply a combination we know (or don't yet know) is empty. (`locked`
                        // is always false in-app now; kept in the guard for isolated/legacy renders.)
                        <button
                            type="button"
                            className="fai-apply"
                            disabled={!canApply || locked || applyHeld}
                            onClick={handleApply}
                            title={conflicts.length ? "This combination can’t show data — adjust your picks" : undefined}
                        >
                            {checking ? "Checking data…" : "Apply filters"}
                        </button>
                    )
                ) : (
                    <div className="fai-preview-note">Preview only.</div>
                )}
                {recordCountAccurate && recordsLabel(feasibility && feasibility.total) && (
                    <span className={`fai-records${validating ? " is-updating" : ""}`} aria-live="polite">
                        {recordsLabel(feasibility.total)}
                    </span>
                )}
            </div>
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A small, copyable trace id so a tester can report exactly which trace to open in the tracing
// dashboard ("it didn't work" -> a request_id you can look up). Click to copy the full id.
const TraceHandle = ({ requestId }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        try {
            if (navigator.clipboard) navigator.clipboard.writeText(requestId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch (e) {
            /* clipboard unavailable — ignore */
        }
    };
    return (
        <button type="button" className="fai-trace-id" onClick={copy} title={`Trace ${requestId} — click to copy`}>
            {copied ? "copied ✓" : `trace ${requestId.slice(0, 8)}`}
        </button>
    );
};

// A chart-command proposal: Otto describes the display change and CONFIRMS before applying (or asks a
// clarifying question when the target chart/value was ambiguous). Reuses the confirm-before-apply
// contract of the filter path — no chart changes without an explicit Apply.
const ChartProposalCard = ({ proposal, onChartApply }) => {
    const [applied, setApplied] = useState(false);
    const canApply = !proposal.needsClarification && (proposal.changes || []).length > 0 && !!onChartApply;
    const apply = () => {
        if (!canApply || applied) return;
        onChartApply(proposal.changes);
        traceExec(proposal, "chart_command.execute", { output: { changes: proposal.changes || [] } });
        setApplied(true);
    };
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            {canApply && (
                <div className="fai-chart-actions">
                    {applied ? (
                        <span className="fai-chart-applied">Done ✓</span>
                    ) : (
                        <button type="button" className="fai-apply" onClick={apply}>
                            Apply
                        </button>
                    )}
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A whole-dashboard reset proposal: Otto CONFIRMS before wiping everything (all filters + every chart's
// display state back to defaults). Same confirm-before-apply contract as the chart card — nothing resets
// without an explicit Apply. Distinct from a filters-only clear (which comes back as a normal filter card).
const ResetProposalCard = ({ proposal, onReset }) => {
    const [applied, setApplied] = useState(false);
    const canApply = !!onReset;
    const apply = () => {
        if (!canApply || applied) return;
        onReset();
        traceExec(proposal, "dashboard_reset.execute", {});
        setApplied(true);
    };
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            {canApply && (
                <div className="fai-chart-actions">
                    {applied ? (
                        <span className="fai-chart-applied">Done ✓</span>
                    ) : (
                        <button type="button" className="fai-apply" onClick={apply}>
                            Reset dashboard
                        </button>
                    )}
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A NAVIGATION proposal: Otto CONFIRMS before taking the user to another page (navigation leaves the
// current page), then drives the app's real router via onNavigate — the same contract the side-menu /
// top-bar buttons use (dirty-form guard, breadcrumbs). The executor is provided by the container (like
// onReset/onChartApply) so this presentational component never imports the redux store. When the
// destination was ambiguous or unreachable it renders as a plain question (no Apply). Same
// confirm-before-act contract as the chart / reset cards.
// `wentThere` is set when the nav-back store still holds the hop THIS card fired: its own state was wiped by
// the navigation's unmount, so without it a rehydrated card would offer "Go to <page>" again while the user is
// already standing there (and right above the "← Back to …" row). Going back pops the hop, which correctly
// re-offers the button.
export const NavProposalCard = ({ proposal, onNavigate, wentThere = false }) => {
    const [clicked, setClicked] = useState(false);
    const navigated = clicked || wentThere;
    const target = proposal.target;
    const canApply = !proposal.needsClarification && !!target && !!onNavigate;
    const go = () => {
        if (!canApply || navigated) return;
        setClicked(true);
        const pushed = onNavigate(target);
        traceExec(proposal, "navigate.execute", {
            output: { target: { key: target.key, label: target.label, url: target.url }, pushed: pushed !== false },
            metadata: { source: "nav_card" }
        });
    };
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            {canApply && (
                <div className="fai-chart-actions">
                    {navigated ? (
                        <span className="fai-chart-applied">Opening {shortPageLabel(target)} ✓</span>
                    ) : (
                        <button type="button" className="fai-apply" onClick={go}>
                            Go to {shortPageLabel(target)}
                        </button>
                    )}
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// The "← Back to <page>" affordance, shown after Otto has TAKEN the user to another page — whichever card or
// chip did it (nav card, cross-capability "open the … page" pick, Q&A deep link / suggestion chip, or a plan's
// navigate step). It is the navigation twin of the filter Undo: same newest-first model (only the most recent
// hop is offered) and the same survive-the-remount persistence (navBackStore), except what it reverts is the
// MOVE — a push back to the page the user was on, with that page's router state and breadcrumb trail restored.
// Filters are untouched: a filter Otto applied keeps its own Undo on its own card.
export const NavBackRow = ({ entry, onNavBack }) => {
    const [going, setGoing] = useState(false);
    const label = (entry && entry.label) || "";
    const go = () => {
        if (going || !onNavBack) return;
        setGoing(true);
        onNavBack();
    };
    return (
        <div className="fai-nav-back-row">
            <button
                type="button"
                className="fai-nav-back"
                onClick={go}
                disabled={going}
                title={label ? `Back to ${label}` : "Back to the previous page"}
            >
                <BackArrowIcon /> {label ? `Back to ${label}` : "Back to the previous page"}
            </button>
        </div>
    );
};

// A TABLE-COMMAND proposal (table_view capability): Otto describes the grounded table operation(s) and
// either auto-applies the cheap/reversible ops (sort / pin / hide / page-size) on mount, or CONFIRMS first
// (resets + export). Drives the page's registered tableController via onTableApply — the same
// container-provided executor contract as onChartApply/onReset/onNavigate, so this presentational card
// imports no page state. A clarification (ambiguous/unknown column) renders as a plain question (no Apply).
// What Otto tells the user once an export actually settles. The report backend decides between building the
// file inline and queueing a background job (its row threshold is server-side), so this is REPORTED, never
// predicted — "check Export History" is only said when the export really was queued.
const EXPORT_OUTCOME_TEXT = {
    downloaded: "Done — the file has downloaded ✓",
    queued: "That's a big export, so it's running in the background. It'll show up under Export History in a few minutes.",
    opened: "Opened the export settings — pick the export type there ✓",
    error: "I couldn't start that export."
};
const exportOutcomeText = outcome => {
    if (!outcome) return null;
    const text = EXPORT_OUTCOME_TEXT[outcome.status];
    if (!text) return null;
    return outcome.status === "error" && outcome.message ? `${text} (${outcome.message})` : text;
};

// What the in-flight PROGRESS bubble says while the report backend builds the file. Present-continuous
// twin of describeTableChange's export vocabulary, and deliberately concrete about WHICH export is running
// (a variant's own name) — the whole point of the bubble is that the user can tell something started.
const EXPORT_FORMAT_NOUN = { excel: "Excel", word: "Word", pdf: "PDF" };
export const exportProgressText = change => {
    if (!change) return null;
    if (change.mode === "advanced") return "Opening the export settings…";
    const fmt = EXPORT_FORMAT_NOUN[String(change.value || "").toLowerCase()] || "";
    if (change.mode === "variant") {
        const label = change.variantLabel || change.variant;
        return `Generating your ${label || "export"}${fmt ? ` (${fmt})` : ""}…`;
    }
    return `Generating your ${fmt || "export"} file…`;
};

// A LONG-RUNNING action, announced as a message of its own. Born for exports: a plan's card plus its option
// buttons buried the card's small "Working on it…" line under them, so a user who picked "Simple Excel" had
// no visible sign anything had started while the backend built the file. This bubble is full-width, bold and
// green, sits at the BOTTOM of the thread (the list auto-scrolls to it), and turns into its own receipt when
// the action settles — one line that carries the whole story instead of a footnote under a card.
// `stale` is a bubble rehydrated from storage (see chatStore.neutralizeMessage): the export it tracked
// finished — or died — on a mount that no longer exists, so it renders frozen rather than spinning forever.
export const ProgressRow = ({ progress }) => {
    const status = (progress && progress.status) || "running";
    const running = status === "running";
    return (
        <div
            className={`fai-progress fai-progress-${status}`}
            data-testid="fai-progress"
            role="status"
            aria-live="polite"
        >
            {running ? (
                <span className="fai-progress-spinner" aria-hidden="true" />
            ) : (
                <span className="fai-progress-icon" aria-hidden="true">
                    {status === "error" ? "⚠" : status === "stale" ? "•" : "✓"}
                </span>
            )}
            <strong className="fai-progress-text">{progress.text}</strong>
        </div>
    );
};

export const TableProposalCard = ({ proposal, onTableApply, onQuestionAnswer, exportReported = false }) => {
    const [applied, setApplied] = useState(false);
    const [outcome, setOutcome] = useState(null);
    // Set when this card's run is an export whose progress + outcome are reported OUTSIDE it (the container's
    // ProgressRow bubble). The card then acknowledges the pick and says nothing more — one export, one status.
    const [ack, setAck] = useState(null);
    // A clarification that declared its answer space (proposal.pendingQuestion) renders it as tappable
    // CHIPS — one per option, plus "All of them" on a multi-pick question — so the user can answer with a
    // tap instead of typing. A tap resolves through the same pending-question path as a typed answer.
    const [answered, setAnswered] = useState(null);
    const pq = proposal.needsClarification && onQuestionAnswer ? proposal.pendingQuestion : null;
    const answer = (label, picks) => {
        if (answered) return;
        setAnswered(label);
        onQuestionAnswer(proposal, picks);
    };
    // An export-mode choice (simple vs the page's export chooser): the buttons carry their own `changes`, and
    // the pick IS the confirmation — same rule as PageChoiceCard.
    const choices = proposal.exportChoices || null;
    const canApply = !proposal.needsClarification && (proposal.changes || []).length > 0 && !!onTableApply;
    // Run `changes` and, when an op reports an outcome (export), surface it on the card once it settles —
    // unless the container is tracking that export in its own progress bubble, in which case the card just
    // acknowledges the pick (`ack`) and leaves the status to the one place that shows it prominently.
    const run = (changes, autoApply, label) => {
        const result = onTableApply(changes);
        traceExec(proposal, "table_command.execute", { output: { changes: changes || [] }, metadata: { autoApply } });
        setApplied(true);
        if (exportReported && (changes || []).some(c => c && c.action === "export")) {
            setAck(label ? `${label} ✓` : "On it ✓");
            return;
        }
        Promise.resolve(result)
            .then(r => {
                const reported = (r && r.outcomes && r.outcomes[0]) || null;
                if (reported) setOutcome(reported);
            })
            .catch(() => {});
    };
    const apply = () => {
        if (!canApply || applied) return;
        run(proposal.changes, false);
    };
    const pick = choice => {
        if (applied) return;
        run(choice.changes || [], false, choice.label);
    };
    // Auto-apply the cheap/reversible ops once on mount (ref-guarded against a re-render double-apply);
    // resets / export (autoApply=false) and any export CHOICE wait for the user.
    const autoAppliedRef = useRef(false);
    useEffect(() => {
        if (!canApply || !proposal.autoApply || autoAppliedRef.current) return;
        autoAppliedRef.current = true;
        run(proposal.changes, true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const showButton = canApply && !proposal.autoApply;
    const outcomeText = exportOutcomeText(outcome);
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            {choices && !!onTableApply && (
                <div className="fai-chart-actions">
                    {applied ? (
                        <span className="fai-chart-applied">{ack || outcomeText || "Working on it…"}</span>
                    ) : (
                        choices.map(c => (
                            <button key={c.kind} type="button" className="fai-apply" onClick={() => pick(c)}>
                                {c.label}
                            </button>
                        ))
                    )}
                </div>
            )}
            {!choices && (showButton || applied) && (
                <div className="fai-chart-actions">
                    {applied ? (
                        <span className="fai-chart-applied">{ack || outcomeText || "Done ✓"}</span>
                    ) : (
                        <button type="button" className="fai-apply" onClick={apply}>
                            Apply
                        </button>
                    )}
                </div>
            )}
            {pq && (
                <div className="fai-chart-actions">
                    {answered ? (
                        <span className="fai-chart-applied">{answered} ✓</span>
                    ) : (
                        <>
                            {(pq.options || []).slice(0, 8).map(o => (
                                <button key={o.id != null ? o.id : o.label} type="button" className="fai-apply" onClick={() => answer(o.label, [o])}>
                                    {o.label}
                                </button>
                            ))}
                            {pq.multi && (pq.options || []).length > 1 && (
                                <button type="button" className="fai-apply" onClick={() => answer("All of them", [...pq.options])}>
                                    All of them
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A MULTI-TASK PLAN (the planner): Otto lists the ordered steps and ALWAYS asks for ONE explicit
// confirmation before running anything (locked UX rule — a plan is never auto-executed). Run hands the
// validated steps to the plan driver (startPlan), which persists them (so a navigation step can't lose the
// rest) and executes them in order. A clarification (vague request / unreachable page) renders as a plain
// question with no Run.
const PlanProposalCard = ({ proposal, onRunPlan }) => {
    const [started, setStarted] = useState(false);
    // Indices the user unchecked. A navigate step stays LOCKED on while a later kept step needs its page
    // (requiredNavIndices), so any runnable selection is always coherent.
    const [deselected, setDeselected] = useState(() => new Set());
    const steps = proposal.steps || [];
    const required = requiredNavIndices(steps, deselected);
    const selectedSteps = selectedPlanSteps(steps, deselected);

    const toggle = i => {
        if (started || required.has(i)) return;
        setDeselected(prev => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });
    };

    const canRun = !proposal.needsClarification && selectedSteps.length > 0 && !!onRunPlan;
    const run = () => {
        if (!canRun || started) return;
        setStarted(true);
        onRunPlan({ ...proposal, steps: selectedSteps });
        traceExec(proposal, "multi_task.run", {
            output: { steps: selectedSteps.map(s => ({ capability: s.capability, target_page: s.target_page, label: s.label })) },
            metadata: { count: selectedSteps.length }
        });
    };
    const runLabel = selectedSteps.length === 1 ? "Run this step" : `Run these ${selectedSteps.length} steps`;

    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            {steps.length > 0 && (
                <ul className="fai-plan-steps">
                    {steps.map((s, i) => {
                        const checked = !deselected.has(i) || required.has(i);
                        return (
                            <li key={i} className={`fai-plan-step${checked ? "" : " is-off"}`}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={started || required.has(i)}
                                        onChange={() => toggle(i)}
                                    />
                                    <span>{s.label}</span>
                                    {required.has(i) && <em className="fai-plan-req"> (needed to get there)</em>}
                                </label>
                            </li>
                        );
                    })}
                </ul>
            )}
            {canRun && (
                <div className="fai-chart-actions">
                    {started ? (
                        <span className="fai-chart-applied">Running ✓</span>
                    ) : (
                        <button type="button" className="fai-apply" onClick={run}>
                            {runLabel}
                        </button>
                    )}
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A CROSS-CAPABILITY clarification: a bare container noun ("show me regions") that Otto can service as a
// filter, a chart grouping, OR a project-level page. Rather than guess, Otto asks — each button re-submits an
// unambiguous directive ("filter by region" / "group the chart by region" / "open the regions page") through
// the SAME send path, so the chosen capability then runs its own normal grounded flow. Confirm-before-act.
const CrossCapabilityCard = ({ proposal, onChoose, onNavigate, onClarify }) => {
    const [chosen, setChosen] = useState(null);
    const pick = choice => {
        if (chosen) return;
        // A "Go to the … page" choice IS the navigation confirmation — navigate IMMEDIATELY rather than
        // re-submitting a directive that would produce a SECOND "want me to take you?" confirm card (the
        // double-clarification). Filter/chart choices re-submit their directive to run that capability's own
        // grounded flow (those don't leave the page, so their confirm/apply step is still wanted).
        if (choice.capability === "navigate" && choice.target && onNavigate) {
            setChosen(`Opening ${shortPageLabel(choice.target)}`);
            const pushed = onNavigate(choice.target);
            traceExec(proposal, "navigate.execute", {
                output: { target: { key: choice.target.key, label: choice.target.label, url: choice.target.url }, pushed: pushed !== false },
                metadata: { source: "cross_capability" }
            });
            return;
        }
        // An entity-noun filter choice (e.g. "Filter by Recommendations") has no real filter VALUE — the noun
        // is the whole dataset. Instead of submitting a literal "filter by recommendation" (which would search
        // for the text), ask WHAT to filter by; the user's typed reply then flows through the normal pipeline.
        if (choice.clarify && onClarify) {
            setChosen(choice.label);
            traceExec(proposal, "cross_capability.execute", { output: { capability: choice.capability, clarify: true } });
            onClarify(choice.clarify);
            return;
        }
        if (!onChoose) return;
        setChosen(choice.label);
        traceExec(proposal, "cross_capability.execute", { output: { capability: choice.capability, directive: choice.directive || null } });
        onChoose(choice.directive);
    };
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            <div className="fai-chart-actions">
                {chosen ? (
                    <span className="fai-chart-applied">{chosen} ✓</span>
                ) : (
                    (proposal.choices || []).map(choice => (
                        <button
                            key={choice.capability}
                            type="button"
                            className="fai-apply"
                            onClick={() => pick(choice)}
                        >
                            {choice.label}
                        </button>
                    ))
                )}
            </div>
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A PAGE-CHOICE clarification: a dashboard filter query that named a table page is valid several ways —
// filter the dashboard, or open that page filtered (and, for Buildings, "filter the dashboard THEN open it"
// since dashboard filters propagate). Otto asks; the pick IS the confirmation (each choice runs its flow via
// onPageChoice — no second card). Buttons come from proposal.choices (page-specific). Falls back to the
// original 2-way if an older proposal without `choices` is rendered.
const PageChoiceCard = ({ proposal, onPageChoice }) => {
    const [chosen, setChosen] = useState(null);
    const choices =
        proposal.choices && proposal.choices.length
            ? proposal.choices
            : [
                  { kind: "here", label: "Filter the dashboard" },
                  { kind: "open-local", label: pageActionLabel("Open", proposal.target) }
              ];
    const pick = choice => {
        if (chosen || !onPageChoice) return;
        setChosen(choice.kind === "here" ? "Filtering the dashboard" : `Opening ${shortPageLabel(proposal.target)}`);
        traceExec(proposal, "page_choice.execute", {
            output: { kind: choice.kind, target: proposal.target && proposal.target.label }
        });
        onPageChoice(choice.kind, proposal);
    };
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            <div className="fai-chart-actions">
                {chosen ? (
                    <span className="fai-chart-applied">{chosen} ✓</span>
                ) : (
                    choices.map(c => (
                        <button key={c.kind} type="button" className="fai-apply" onClick={() => pick(c)}>
                            {c.label}
                        </button>
                    ))
                )}
            </div>
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A CAPABILITY-REDIRECT choice: the request can't run on this page, several reachable pages could run it,
// and the user didn't name one. Each button carries that page's COMPLETE navigate+replay plan (built by
// capabilityRedirect), so picking one both moves the user and performs what they originally asked — the pick
// is the single confirmation, exactly like PageChoiceCard. The predecessor of this card was a plain sentence:
// the user's answer went back through the classifier as a bare page name and Otto offered a bare navigation,
// dropping the request. Typed answers are handled by the pending-clarification store and land on the SAME
// steps, so tapping and typing can't diverge.
export const RedirectChoiceCard = ({ proposal, onRunPlan }) => {
    const [chosen, setChosen] = useState(null);
    const pick = choice => {
        if (chosen || !onRunPlan || !(choice.steps || []).length) return;
        setChosen(`Opening ${shortPageLabel(choice.target)}`);
        traceExec(proposal, "capability_redirect.execute", {
            output: { target: choice.key, steps: (choice.steps || []).map(s => s.capability), source: "card" }
        });
        onRunPlan({ planId: `${proposal.planId || proposal.requestId}-${choice.key}`, steps: choice.steps });
    };
    return (
        <div className="fai-msg fai-assistant">
            <div className="fai-chart-msg">{proposal.message}</div>
            <div className="fai-chart-actions">
                {chosen ? (
                    <span className="fai-chart-applied">{chosen} ✓</span>
                ) : (
                    (proposal.choices || []).map(c => (
                        <button key={c.key} type="button" className="fai-apply" onClick={() => pick(c)}>
                            {c.label}
                        </button>
                    ))
                )}
            </div>
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// A REMOVE-A-FILTER proposal (grounded against the ACTIVE filters — see orchestrator.buildRemovalProposal).
// Unlike the add-a-filter card, a matched removal AUTO-APPLIES (user decision): on mount it fires onApply
// once with the removal payload, which (like any apply) snapshots for Undo and accumulates onto the applied
// stack. When nothing matched an applied filter it applies nothing — it just shows the "not currently
// applied" message + the list of filters that ARE. `applied`/`undoable` mirror AssistantProposal:
// `applied` = this removal is on the stack; `undoable` = it's the most recent apply (so it shows Undo, else
// a static "Applied"). `locked` is retained (always false now) so the component keeps working in isolation.
export const RemovalProposalCard = ({ proposal, maps, applied, locked, undoable = true, onApply, onUndo }) => {
    const removedNames = (proposal.removals || []).map(r => `${fieldLabel(r.field, maps)}: ${r.name}`);
    const clearedLabels = [
        ...(proposal.clears || []).map(c => c.label || fieldLabel(c.id, maps)),
        ...(proposal.clearWildcards || []).map(c => (c && c.label) || fieldLabel(c && c.id != null ? c.id : c, maps)),
        ...(proposal.clearYear ? ["Years"] : [])
    ];
    const hasTargets = removedNames.length > 0 || clearedLabels.length > 0;
    // When another Otto filter is live, this removal can't auto-apply (one live action at a time). Don't show
    // the past-tense "Done — I removed…" copy in that case — it would claim something that didn't happen.
    const blockedByLock = hasTargets && !applied && locked;

    // Fire the removal exactly once, on mount, when it can apply (there's something to remove and no other
    // card holds the live-action lock). Ref-guarded so a re-render (the parent flipping this card to
    // `applied`) can't double-apply.
    const autoAppliedRef = useRef(false);
    useEffect(() => {
        if (!onApply || applied || locked || !hasTargets || autoAppliedRef.current) return;
        autoAppliedRef.current = true;
        onApply(buildRemovalPayload(proposal), { requestId: proposal.requestId, sessionId: proposal.sessionId });
    }, [onApply, applied, locked, hasTargets, proposal]);

    return (
        <div className="fai-msg fai-assistant">
            {!blockedByLock && proposal.message && <div className="fai-say">{proposal.message}</div>}
            {hasTargets && !blockedByLock && (
                <div className="fai-chips">
                    {removedNames.map((t, i) => (
                        <Chip key={`rm${i}`} tone="ok">
                            {applied && <CheckIcon />} removed {t}
                        </Chip>
                    ))}
                    {clearedLabels.map((t, i) => (
                        <Chip key={`cl${i}`} tone="ok">
                            {applied && <CheckIcon />} cleared {t}
                        </Chip>
                    ))}
                </div>
            )}
            {blockedByLock && (
                <div className="fai-preview-note">
                    You have another Otto filter applied — undo it first, then ask me again to remove{" "}
                    {[...removedNames, ...clearedLabels].join(", ")}.
                </div>
            )}
            {applied &&
                onUndo &&
                (undoable ? (
                    <button type="button" className="fai-undo" onClick={onUndo} title="Revert this change">
                        <UndoIcon /> Undo
                    </button>
                ) : (
                    <div className="fai-applied-static">
                        <CheckIcon /> Applied
                    </div>
                ))}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// "Which filters are active right now?" (router kind `state_query`) — a READ of the page's own filter state,
// so there is nothing to confirm, apply, or undo. The headline carries the count; each applied dimension is
// one chip with the option NAMES the filter window shows (wildcards and the year range come through the same
// summary). It reports its rendered text to /turns/display like the main card, so a resumed conversation
// replays the answer the user saw rather than an empty classifier turn.
export const ActiveFiltersCard = ({ proposal }) => {
    const groups = proposal.groups || [];
    const reportedRef = useRef(false);
    useEffect(() => {
        if (reportedRef.current || !proposal.requestId) return;
        reportedRef.current = true;
        try {
            if (typeof reportDisplayedText === "function") {
                const p = reportDisplayedText({
                    requestId: proposal.requestId,
                    message: [proposal.message, proposal.summaryText].filter(Boolean).join("\n")
                });
                if (p && typeof p.catch === "function") p.catch(() => {});
            }
        } catch (e) {
            /* best-effort side channel — never break the card */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fai-msg fai-assistant">
            {proposal.message && <div className="fai-say">{proposal.message}</div>}
            {!!groups.length && (
                <div className="fai-chips">
                    {groups.map((g, i) => (
                        <Chip key={`af${i}`} tone="ok">
                            {g.names && g.names.length ? `${g.label}: ${g.names.join(", ")}` : g.label}
                        </Chip>
                    ))}
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

// Minimal, injection-SAFE markdown formatter for Otto's Q&A answer. Everything is rendered as React text
// nodes / elements — never dangerouslySetInnerHTML — so a model-authored answer can't inject markup. It
// handles the small subset the platform_qa answers use: blank-line-separated paragraphs, single line breaks
// (kept as <br/>), `- ` / `* ` bullet lists, and inline **bold**. Anything else renders as its literal text.
// (No new npm dependency — the repo has no react-markdown as a direct dependency.)
const renderQaInline = (text, keyPrefix) => {
    // Split on **bold** spans; the odd segments are the emphasized text.
    const parts = String(text == null ? "" : text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
            <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>
        ) : (
            <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>
        )
    );
};

const renderQaMarkdown = content => {
    const lines = String(content == null ? "" : content).split(/\r?\n/);
    const blocks = [];
    let para = []; // buffered consecutive text lines -> one <p> (single breaks kept as <br/>)
    let bullets = []; // buffered consecutive `- ` items -> one <ul>

    const flushPara = () => {
        if (!para.length) return;
        const key = `qa-p-${blocks.length}`;
        blocks.push(
            <p className="fai-qa-p" key={key}>
                {para.map((ln, i) => (
                    <React.Fragment key={`${key}-l${i}`}>
                        {i > 0 && <br />}
                        {renderQaInline(ln, `${key}-l${i}`)}
                    </React.Fragment>
                ))}
            </p>
        );
        para = [];
    };
    const flushBullets = () => {
        if (!bullets.length) return;
        const key = `qa-ul-${blocks.length}`;
        blocks.push(
            <ul className="fai-qa-list" key={key}>
                {bullets.map((b, i) => (
                    <li key={`${key}-i${i}`}>{renderQaInline(b, `${key}-i${i}`)}</li>
                ))}
            </ul>
        );
        bullets = [];
    };

    lines.forEach(raw => {
        const trimmed = raw.trim();
        if (!trimmed) {
            // Blank line -> flush the current block(s): paragraph / list break.
            flushBullets();
            flushPara();
            return;
        }
        const bullet = trimmed.match(/^[-*]\s+(.*)$/);
        if (bullet) {
            flushPara();
            bullets.push(bullet[1]);
            return;
        }
        flushBullets();
        para.push(trimmed);
    });
    flushBullets();
    flushPara();
    return blocks;
};

// How long a silent in-flight turn waits before the thinking bubble admits it's slow. A normal turn lands
// in 2–20s; the SSE guards allow up to ~120s (stall/total timers) + a fallback POST before erroring, so a
// wedged upstream looks EXACTLY like a hang for minutes with nothing changing on screen — that unexplained
// silence is what got "show me fci" reported as frozen (FTO-13). The turn itself needs no user action; the
// notice just makes the wait honest.
export const SLOW_TURN_NOTICE_MS = 20000;

// The bubble shown while a turn is in flight. Over the streamed transport it fills in as the turn happens:
// first what Otto is doing (the classifier's route, ~1s in), then the answer itself once prose starts
// arriving. Both are DISPLAY-ONLY — the real card, built from the complete response, replaces this bubble
// wholesale — so nothing here is ever the final word, and with no stream it is just the old "Thinking…".
export const ThinkingBubble = ({ route = null, text = "" }) => {
    const [slow, setSlow] = useState(false);
    // Re-arm when prose starts arriving (progress is visible then); a bubble that mounts and stays silent
    // past the threshold owns up to it.
    useEffect(() => {
        if (text) {
            setSlow(false);
            return undefined;
        }
        const t = setTimeout(() => setSlow(true), SLOW_TURN_NOTICE_MS);
        return () => clearTimeout(t);
    }, [text]);
    return (
        <div className="fai-msg fai-assistant" data-testid="fai-thinking">
            {text ? (
                // Same markdown renderer and typography as the finished answer, so the text doesn't reflow when
                // the card takes over. The caret (.fai-streaming) replaces the loader: a spinner beside visibly
                // arriving text reads as a second, competing progress indicator.
                <div className="fai-qa-answer fai-streaming">{renderQaMarkdown(text)}</div>
            ) : (
                <>
                    {thinkingLabel(route)} <DnaLoader />
                    {slow && (
                        <div className="fai-refine">
                            Still working — this one’s slower than usual. I’ll answer or say so within a couple of
                            minutes; no need to resend.
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// One deep-link button ("Take me there"). Mirrors NavProposalCard's onNavigate contract EXACTLY: on click it
// fires onNavigate(target) once (drives the app's real router) and flips to an "Opening … ✓" confirmation.
// Each deep link owns its own navigated state so multiple links on one answer act independently.
const QaDeepLinkButton = ({ deepLink, onNavigate, trace }) => {
    const [navigated, setNavigated] = useState(false);
    const target = deepLink && deepLink.target;
    const label = (deepLink && deepLink.label) || "";
    const go = () => {
        if (!target || !onNavigate || navigated) return;
        setNavigated(true);
        const pushed = onNavigate(target);
        traceExec(trace, "navigate.execute", {
            output: { target: { key: target.key, label: target.label, url: target.url }, pushed: pushed !== false },
            metadata: { source: "qa_deeplink" }
        });
    };
    if (!target) return null;
    return navigated ? (
        <span className="fai-chart-applied">Opening {label || "page"} ✓</span>
    ) : (
        <button type="button" className="fai-apply fai-qa-link" onClick={go}>
            Take me there{label ? `: ${label}` : ""}
        </button>
    );
};

// Renders the platform_qa (platform Q&A) answer inside Otto's chat. Consumes the pinned proposal contract:
//   proposal.qa = { answered, answer (markdown), assistantMessage, citations:[{key,label}], deepLinks:[{label,target}] }
// answered === true  -> a full answer card: the assistantMessage lead line, the formatted markdown answer, a
//                       row of citation "source" chips (one per citations[].label), and one "Take me there"
//                       deep-link button per deepLinks[] (each fires onNavigate(target) — the SAME executor
//                       contract NavProposalCard uses). Standard FeedbackBar + TraceHandle attached like every
//                       other card. The deep-link row is guarded on onNavigate so a mount without a nav
//                       executor still shows the answer + sources (just no navigation buttons).
// answered === false -> a refusal: a plain Otto bubble showing assistantMessage only (no chips, no buttons),
//                       same style as the smalltalk/clarify plain bubble, with FeedbackBar + TraceHandle.
// Presentational only — markdown is handled here by the injection-safe formatter above; navigation is
// delegated to the container-provided onNavigate executor (so this component imports no store/router).
// One "you can also…" action-suggestion chip (Phase 0 / Route A). A NAVIGATE suggestion drives the real
// router (onNavigate); a DIRECTIVE suggestion re-submits its phrase through the normal pipeline (onChoose =
// submitText) so the target capability runs its OWN grounding + confirm — nothing is auto-executed here.
// Each chip owns a tiny "picked" state so it reads as acted-on after a tap.
const QaSuggestionChip = ({ suggestion, onNavigate, onChoose, trace }) => {
    const [picked, setPicked] = useState(false);
    const act = () => {
        if (picked) return;
        if (suggestion.kind === "navigate" && suggestion.target && onNavigate) {
            setPicked(true);
            const pushed = onNavigate(suggestion.target);
            traceExec(trace, "navigate.execute", {
                output: { target: { key: suggestion.target.key, label: suggestion.target.label, url: suggestion.target.url }, pushed: pushed !== false },
                metadata: { source: "qa_suggestion" }
            });
            return;
        }
        if (suggestion.kind === "directive" && suggestion.directive && onChoose) {
            setPicked(true);
            traceExec(trace, "platform_qa.suggest", { output: { directive: suggestion.directive, label: suggestion.label } });
            onChoose(suggestion.directive);
        }
    };
    return (
        <button type="button" className="fai-apply fai-qa-suggestion" onClick={act} disabled={picked}>
            {picked ? `${suggestion.label} ✓` : suggestion.label}
        </button>
    );
};

// One citation ("source") chip. A citation that names a PAGE Otto can open carries a grounded `target`
// (qaTargets.js), and then the chip is the affordance it looks like: tapping it navigates there — the answer
// says where the feature lives, and the chip beside it takes you. Anything else (a concept, an action, a page
// that grounded nowhere) renders exactly as before: a plain, inert source label. Same onNavigate contract as
// every other Otto navigation, so the "← Back to …" row and the trace span come along for free.
const QaCitationChip = ({ citation, onNavigate, trace }) => {
    const [navigated, setNavigated] = useState(false);
    const target = citation && citation.target;
    const label = (citation && citation.label) || "";
    if (!target || !onNavigate) return <Chip tone="neutral">{label}</Chip>;
    const go = () => {
        if (navigated) return;
        setNavigated(true);
        const pushed = onNavigate(target);
        traceExec(trace, "navigate.execute", {
            output: { target: { key: target.key, label: target.label, url: target.url }, pushed: pushed !== false },
            metadata: { source: "qa_citation" }
        });
    };
    return (
        <button
            type="button"
            className="fai-chip fai-chip-neutral fai-qa-source-link"
            onClick={go}
            disabled={navigated}
            // The chip's text names a SOURCE ("Project Settings Category"); the tooltip and the accessible
            // name say what tapping it does, so neither a hover nor a screen reader has to infer it.
            title={`Open ${target.label || label}`}
            aria-label={`Open ${target.label || label}`}
        >
            {label}
            {navigated ? <CheckIcon /> : <OpenPageIcon />}
        </button>
    );
};

export const QaAnswerCard = ({ proposal, onNavigate, onChoose }) => {
    const qa = (proposal && proposal.qa) || {};
    const assistantMessage = qa.assistantMessage || proposal.message || "";

    // Refusal: a plain bubble, message only (no sources, no deep links).
    if (!qa.answered) {
        return (
            <div className="fai-msg fai-assistant">
                <div className="fai-say">{assistantMessage}</div>
                <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
                {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
            </div>
        );
    }

    const citations = Array.isArray(qa.citations) ? qa.citations : [];
    const deepLinks = Array.isArray(qa.deepLinks) ? qa.deepLinks : [];
    // Grounded action suggestions (navigate needs onNavigate; directive needs onChoose) — hide any whose
    // executor isn't wired on this mount, so a chip never dead-ends.
    const suggestions = (Array.isArray(qa.suggestions) ? qa.suggestions : []).filter(
        s => s && (s.kind === "navigate" ? !!onNavigate : s.kind === "directive" ? !!onChoose : false)
    );

    return (
        <div className="fai-msg fai-assistant fai-qa">
            {assistantMessage && <div className="fai-say fai-qa-lead">{assistantMessage}</div>}
            {qa.answer && <div className="fai-qa-answer">{renderQaMarkdown(qa.answer)}</div>}
            {citations.length > 0 && (
                <div className="fai-qa-sources">
                    {citations.map((c, i) => (
                        <QaCitationChip
                            key={c && c.key != null ? c.key : `src${i}`}
                            citation={c}
                            onNavigate={onNavigate}
                            trace={{ requestId: proposal.requestId, sessionId: proposal.sessionId }}
                        />
                    ))}
                </div>
            )}
            {deepLinks.length > 0 && onNavigate && (
                <div className="fai-chart-actions fai-qa-links">
                    {deepLinks.map((dl, i) => (
                        <QaDeepLinkButton key={i} deepLink={dl} onNavigate={onNavigate} trace={{ requestId: proposal.requestId, sessionId: proposal.sessionId }} />
                    ))}
                </div>
            )}
            {suggestions.length > 0 && (
                <div className="fai-qa-suggestions">
                    <div className="fai-qa-suggestions-lead">You can also:</div>
                    <div className="fai-chart-actions">
                        {suggestions.map((s, i) => (
                            <QaSuggestionChip key={i} suggestion={s} onNavigate={onNavigate} onChoose={onChoose} trace={{ requestId: proposal.requestId, sessionId: proposal.sessionId }} />
                        ))}
                    </div>
                </div>
            )}
            <FeedbackBar requestId={proposal.requestId} utterance={proposal.utterance} />
            {proposal.requestId && <TraceHandle requestId={proposal.requestId} />}
        </div>
    );
};

const Message = ({ message, idx, maps, appliedStack, undoableIdx, onApply, onUndo, onChartApply, onReset, onNavigate, navHop, onTableApply, onRunPlan, onPageChoice, onChoose, onClarify, onQuestionAnswer, feasibilityCtx, onOpenFilterWindow }) => {
    // This card's live state under the accumulate model: `applied` = its filter is on the dashboard;
    // `undoable` = it's the most recent apply (only that one exposes an Undo). No card is ever `locked` —
    // successive questions stay interactive so their filters can be applied and accumulated.
    const applied = (appliedStack || []).includes(idx);
    const undoable = undoableIdx === idx;
    // Every navigation a card fires goes out through the container's navigateTo with THIS message's index and
    // trace ids attached, so the "← Back to …" row can anchor under the card that navigated and its span joins
    // the turn's trace. The cards keep their simple onNavigate(target) contract — this is the only place that
    // knows which message they belong to.
    const navigate = onNavigate
        ? target =>
              onNavigate(target, {
                  msgIdx: idx,
                  trace: message.proposal
                      ? { requestId: message.proposal.requestId, sessionId: message.proposal.sessionId }
                      : null
              })
        : undefined;
    if (message.role === "user")
        return (
            <Row who="user">
                <div className="fai-bubble fai-bubble-user">
                    <div className="fai-msg fai-user">{message.text}</div>
                    {message.ts && <div className="fai-timestamp">{formatTime(message.ts)}</div>}
                </div>
            </Row>
        );
    // A long-running action (today: an export the report backend is building) — full-width, loud, and its
    // own message, so it can't be read as a footnote on the card above it.
    if (message.progress) return <ProgressRow progress={message.progress} />;
    // System status lines (e.g. "Cleared all filters.") stay centered with no avatar.
    if (message.system) return <div className="fai-msg fai-system">{message.system}</div>;
    if (message.error)
        return (
            <Row who="assistant">
                <div className="fai-msg fai-assistant fai-error">{message.error}</div>
            </Row>
        );
    // Chart-display command: its own confirm/clarify card (no filter apply card).
    if (message.proposal && message.proposal.chart)
        return (
            <Row who="assistant">
                <ChartProposalCard proposal={message.proposal} onChartApply={onChartApply} />
            </Row>
        );
    // Whole-dashboard reset: its own confirm card (resets filters + chart display state).
    if (message.proposal && message.proposal.reset)
        return (
            <Row who="assistant">
                <ResetProposalCard proposal={message.proposal} onReset={onReset} />
            </Row>
        );
    // Navigation: confirm-before-navigate card that drives the app router (grounded destination).
    if (message.proposal && message.proposal.navigate)
        return (
            <Row who="assistant">
                <NavProposalCard proposal={message.proposal} onNavigate={navigate} wentThere={!!navHop} />
            </Row>
        );
    // Table command (table_view): grounded sort / columns / pagination / reset / export card. Cheap ops
    // auto-apply on mount; resets/export confirm. Drives the page's tableController via onTableApply.
    // `exportReported`: inside the container, onTableApply is always the export-tracking wrapper
    // (applyTableChanges), so an export's progress and outcome belong to the ProgressRow bubble — the card
    // only acknowledges the pick. A card rendered standalone (tests, storybook-style use) keeps reporting
    // its own outcome, since nothing else would.
    if (message.proposal && message.proposal.table)
        return (
            <Row who="assistant">
                <TableProposalCard proposal={message.proposal} onTableApply={onTableApply} onQuestionAnswer={onQuestionAnswer} exportReported />
            </Row>
        );
    // Multi-task plan: the ordered steps + ONE explicit confirmation for the whole plan.
    if (message.proposal && message.proposal.plan)
        return (
            <Row who="assistant">
                <PlanProposalCard proposal={message.proposal} onRunPlan={onRunPlan} />
            </Row>
        );
    // Page-choice: filter the dashboard vs open the Recommendations page filtered.
    if (message.proposal && message.proposal.pageChoice)
        return (
            <Row who="assistant">
                <PageChoiceCard proposal={message.proposal} onPageChoice={onPageChoice} />
            </Row>
        );
    // Capability redirect with several possible pages: one button per page, each the whole navigate+replay
    // plan for it. Must precede the plain clarify bubble below — this question is answerable, not just text.
    if (message.proposal && message.proposal.redirectChoice)
        return (
            <Row who="assistant">
                <RedirectChoiceCard proposal={message.proposal} onRunPlan={onRunPlan} />
            </Row>
        );
    // Cross-capability clarification: a bare container noun that could be a filter, a chart grouping, or a
    // page. Buttons re-submit an unambiguous directive through the normal flow (onChoose = submitText).
    if (message.proposal && message.proposal.crossCapability)
        return (
            <Row who="assistant">
                <CrossCapabilityCard proposal={message.proposal} onChoose={onChoose} onNavigate={navigate} onClarify={onClarify} />
            </Row>
        );
    // Remove-a-filter: grounded against the active filters, auto-applies a match (see RemovalProposalCard).
    // Shares the accumulate/Undo model with the add-a-filter cards via the applied stack.
    if (message.proposal && message.proposal.removal)
        return (
            <Row who="assistant">
                <RemovalProposalCard
                    proposal={message.proposal}
                    maps={maps}
                    applied={applied}
                    locked={false}
                    undoable={undoable}
                    onApply={onApply ? (payload, trace) => onApply(idx, payload, trace) : undefined}
                    onUndo={onUndo}
                />
            </Row>
        );
    // State query (state_query): "which filters are active right now?" — a read-only answer built from the
    // page's own applied filters (no Apply, no Undo).
    if (message.proposal && message.proposal.filterState)
        return (
            <Row who="assistant">
                <ActiveFiltersCard proposal={message.proposal} />
            </Row>
        );
    // Platform Q&A (platform_qa): a grounded answer about how FCA works / what the data means. `answered`
    // true renders the full QaAnswerCard (answer markdown + citation source chips + "Take me there" deep-link
    // buttons); `answered` false renders a plain refusal bubble. Both live inside the exported QaAnswerCard so
    // each is unit-testable in isolation (the split is on proposal.qa.answered). Deep links reuse onNavigate.
    if (message.proposal && message.proposal.qa)
        return (
            <Row who="assistant">
                <QaAnswerCard proposal={message.proposal} onNavigate={navigate} onChoose={onChoose} />
            </Row>
        );
    // Greeting / chit-chat, or an open clarifying question (e.g. "what would you like to filter by?"): a plain
    // Otto reply, no apply card. The user answers by typing, which flows through the normal pipeline.
    // fai-multiline: a rehydrated history bubble replays the card's DISPLAYED text, which is multi-line
    // (headline / lead / options) — preserve its line breaks.
    if (message.proposal && (message.proposal.smalltalk || message.proposal.clarify))
        return (
            <Row who="assistant">
                <div className="fai-msg fai-assistant fai-multiline">{message.proposal.message}</div>
            </Row>
        );
    return (
        <Row who="assistant">
            <AssistantProposal
                proposal={message.proposal}
                maps={maps}
                applied={applied}
                locked={false}
                undoable={undoable}
                onApply={onApply ? (payload, trace) => onApply(idx, payload, trace) : undefined}
                onUndo={onUndo}
                feasibilityCtx={feasibilityCtx}
                autoRun={!!message.autoRun}
                onOpenFilterWindow={onOpenFilterWindow}
            />
        </Row>
    );
};

const FilterAssistant = ({ projectId, projectIds, buildingIds, dataView = "project", activeFilters, filterFields, filtersReady = true, fieldsMeta, onApply, onUndo, chartContext, onChartApply, onReset, navTargets, page = "dashboard", onNavigate, tableController, onTableApply, onOpenFilterWindow, candidateSource = null }) => {
    // Rehydrate any conversation persisted from a previous page/refresh (read once on mount). Only the
    // Clear button empties this — navigating between pages or reloading restores the chat as the user left it.
    const [restored] = useState(() => loadChat() || {});
    const [open, setOpen] = useState(!!restored.open);
    // ---- What's New (FTO-128) -------------------------------------------------------------------------
    // The deck opens on a USER-initiated open ONLY. `setOpen(true)` is also called further down to resume a
    // pending plan, and interrupting a plan the user is halfway through with an announcement would be a
    // regression — so the launcher marks its own opens and nothing else does.
    const userOpenedRef = useRef(false);
    const [newsDeck, setNewsDeck] = useState(null);
    const [newsUnseen, setNewsUnseen] = useState(false);
    // Is there a published deck at all for this user? Drives whether the header even offers the control —
    // a button that opens nothing is worse than no button. Drafts (clips not yet recorded) count as none.
    const newsAvailable = useMemo(() => buildDeck({ lastSeen: null }).length > 0, []);
    const openPanel = useCallback(() => {
        userOpenedRef.current = true;
        setOpen(o => !o);
    }, []);
    const closeNews = useCallback(() => {
        setNewsDeck(null);
        setNewsUnseen(false);
        // Any exit counts as seen — a user who dismisses on slide one has decided.
        markSeen(OTTO_VERSION);
    }, []);
    const reopenNews = useCallback(() => {
        // Deliberately `lastSeen: null`: asking for it explicitly means "show me the current release's
        // deck", whether or not it was already dismissed.
        const deck = buildDeck({ lastSeen: null });
        if (deck.length) setNewsDeck(deck);
    }, []);
    const [schema, setSchema] = useState(null);
    const [schemaError, setSchemaError] = useState(false);
    // Dynamic schema: maps + the available-field list are derived from the dashboard's live, scoped
    // filterFields (enriched by the static /schema), so the assistant only considers what the user sees.
    const maps = useMemo(
        () => (schema ? augmentMapsWithFilterFields(buildSchemaMaps(schema), filterFields || []) : null),
        [schema, filterFields]
    );
    const availableFields = useMemo(() => buildAvailableFields(filterFields || []), [filterFields]);
    // Context each proposal card needs to run its ORDER-AWARE feasibility check (multi-select): the
    // catalog scope for /filter_catalog cascades + the dashboard's live applied filters. Identity churns
    // with parent renders (activeFilters is rebuilt each render); the card guards its effect with
    // stringified keys, so churn here is harmless.
    const feasibilityCtx = useMemo(
        // filterFields ride along so the card can model SELECTED wildcard pills into the /get_list
        // conditioning exactly as Apply would (buildDashboardFilterParams needs the field configs).
        () => ({ scope: { projectId, projectIds, buildingIds }, activeFilters, filterFields: filterFields || [] }),
        [projectId, projectIds, buildingIds, activeFilters, filterFields]
    );
    // Dev-only: expose Otto's exact field view (the assembled filterFields it can apply + the
    // available_fields it sends to /interpret) so local e2e can diff it against the filter window and
    // confirm no filter is missed. Never set in production; safe to remove.
    useEffect(() => {
        if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
            window.__OTTO_FIELDS__ = { filterFields: filterFields || [], availableFields };
        }
    }, [filterFields, availableFields]);
    // Background pre-fetch: once the schema + the COMPLETE filter set are ready, warm the candidate-option
    // cache for every controlled-vocabulary field in the background. This moves the slow per-field /get_list
    // fan-out OFF Otto's critical path — by the time the user finishes typing their first query the options
    // are already cached (and, via the persistent cache, stay warm across reloads). Runs once per project;
    // ref-guarded so it can't re-fire as filterFields churn while the dynamic bands load.
    const warmedRef = useRef(null);
    useEffect(() => {
        if (!maps || !filtersReady) return;
        // Re-key on buildingIds too: the per-user building scope (D2) loads after open, so a late arrival
        // should re-warm the now-scoped catalog rather than keep the unscoped one.
        const projectKey = String(projectId != null ? projectId : (projectIds && projectIds.join(",")) || "");
        const key = `${projectKey}#${(buildingIds && buildingIds.join(",")) || ""}`;
        if (warmedRef.current === key) return;
        warmedRef.current = key;
        warmCatalog({ maps, projectId, projectIds, buildingIds }); // fire-and-forget
    }, [maps, filtersReady, projectId, projectIds, buildingIds]);
    // Startup connectivity report (console, once per page per load): backend URL + /health (which
    // carries the backend's OWN dependency state: Postgres, OpenAI key) + /schema + /config. Both
    // calls below are deduped inside startupDiagnostics, so dep churn re-running them is a no-op.
    useEffect(() => {
        runStartupDiagnostics({ page, projectId, projectIds, userId: readUserId() });
    }, [page, projectId, projectIds]);
    // ...and a second line once the async field bands land — "mounted fine but the filter set never
    // became ready/stayed empty" is the degraded state worth catching.
    useEffect(() => {
        if (!maps || !filtersReady) return;
        reportFiltersReady({ page, fieldCount: (filterFields || []).length, navTargetCount: (navTargets || []).length });
    }, [maps, filtersReady, page, filterFields, navTargets]);
    // Should the What's New deck open? Every condition below is a reason NOT to interrupt, which is the
    // right default for something the user did not ask for.
    useEffect(() => {
        if (!open || !newsAvailable) return undefined;
        // Consumed on read: a re-render while the panel stays open must not re-trigger the deck.
        const userInitiated = userOpenedRef.current;
        userOpenedRef.current = false;
        let cancelled = false;
        hydrateSeen().then(({ version, resolved }) => {
            if (cancelled) return;
            // FAIL CLOSED. An unreachable (or not-yet-deployed) preference store means we cannot know what
            // this user has already dismissed, and announcing the same release on every page load is far
            // worse than announcing it a session late.
            if (!resolved) return;
            const deck = buildDeck({ lastSeen: dismissedVersion() || version });
            setNewsUnseen(deck.length > 0);
            if (!deck.length || !userInitiated) return;
            // Never talk over an open plan or an unanswered question — both are conversations the user
            // started and is waiting on.
            if (loadPlan() || loadQuestion(page)) return;
            setNewsDeck(deck);
        });
        return () => {
            cancelled = true;
        };
    }, [open, newsAvailable, page]);
    const [messages, setMessages] = useState(restored.messages || []);
    const [input, setInput] = useState("");
    const inputRef = useRef(null); // the composer textarea, so it can be autosized to its content
    const [activeModal, setActiveModal] = useState(null); // "actions" | "examples" | "recommendations" | null
    // How many examples are revealed per group in the Examples modal (title -> count). Absent = EXAMPLES_STEP.
    // Reset each time the Examples modal opens so it always starts collapsed.
    const [exampleCounts, setExampleCounts] = useState({});
    // ---- what this mount can actually do, for the Actions button in the composer --------------------
    // The capabilities this mount really handed executors for. The page registry is the DECLARED truth and
    // these props are the LIVE one; describeCapabilities intersects them, because a page that declares a
    // capability the mount never wired cannot run it, and the Actions panel must not promise anything the
    // page will not deliver. This is also why it is derived from props rather than listed per mount: a mount
    // that gains an executor starts advertising it, and one that loses it stops, with no second edit.
    const wiredCapabilities = useMemo(() => {
        const wired = [];
        if (onApply) wired.push("filters");
        if (onChartApply) wired.push("charts");
        if (tableController && onTableApply) wired.push("table_view");
        if (onReset) wired.push("dashboard_reset");
        // navTargets is the catalog nav GROUNDS against — an empty one means navigation silently can't work,
        // so it is not something to offer (see the "navTargets required per mount" rule).
        if (onNavigate && (navTargets || []).length) wired.push("navigate");
        return wired;
    }, [onApply, onChartApply, tableController, onTableApply, onReset, onNavigate, navTargets]);
    const pageActions = useMemo(() => describeCapabilities(page, wiredCapabilities), [page, wiredCapabilities]);
    // The one set every page-scoped affordance below filters on — the INTERSECTION, not the raw props, so the
    // examples agree with the Actions panel about what this page can do.
    const availableCapabilities = useMemo(() => new Set(pageActions.map(a => a.key)), [pageActions]);
    // The canned prompts, scoped the same way: no page offers an example prompt it would only clarify on.
    const pageExamples = useMemo(
        () => EXAMPLE_CATEGORIES.filter(c => !c.needs || availableCapabilities.has(c.needs)),
        [availableCapabilities]
    );
    const pageRecommendations = useMemo(
        () => RECOMMENDATIONS.filter(r => !r.needs || availableCapabilities.has(r.needs)),
        [availableCapabilities]
    );
    // How Otto names the page in the Actions panel heading — "Recommendations Table View", "FCA Dashboard".
    const pageTitle = useMemo(
        () => (isKnownPage(page) ? shortPageLabel({ key: page, label: pageLabelOf(page) }) : ""),
        [page]
    );
    const [showHistory, setShowHistory] = useState(false);
    const [loading, setLoading] = useState(false);
    // What the in-flight turn has told us so far, over the streamed transport: the route the classifier
    // picked, and any model-authored prose that has arrived. DISPLAY-ONLY — both are wiped the moment the
    // real proposal lands, which renders from the complete response. See core/service.interpretUtterance.
    const [streamRoute, setStreamRoute] = useState(null);
    const [streamText, setStreamText] = useState("");
    const [pendingText, setPendingText] = useState(null); // a query typed before the filter set finished loading
    const [sessionId, setSessionId] = useState(restored.sessionId || null);
    // The message indices whose filters are currently APPLIED to the dashboard, in the order they were
    // applied — a STACK. Successive questions no longer freeze each other: each Apply ACCUMULATES on the
    // dashboard (the same merge a manual apply does) and pushes its idx here, so every card stays fully
    // interactive. The dashboard keeps a matching snapshot stack, so Undo is newest-first: only the top of
    // this stack shows an interactive Undo (undoing out of order can't be made coherent with nested
    // snapshots), and undoing it pops back to the previous applied card. Rehydrated from the persisted chat
    // (with a one-time migration from the old single-index field) so applied/Undo survives navigation + reload.
    const [appliedStack, setAppliedStack] = useState(() => {
        if (Array.isArray(restored.appliedStack)) return restored.appliedStack;
        return typeof restored.appliedFilterIdx === "number" ? [restored.appliedFilterIdx] : [];
    });
    const undoableIdx = appliedStack.length ? appliedStack[appliedStack.length - 1] : null;
    // The most recent page Otto TOOK the user away from, or null — what the "← Back to …" row offers. Seeded
    // from the nav-back store because the hop was recorded by the PREVIOUS mount (the origin page's Otto),
    // which the navigation unmounted; the store, not React state, is what crosses that boundary.
    const [navBack, setNavBack] = useState(() => peekNavBack());
    const [enabled, setEnabled] = useState(true); // optimistic; hide launcher if /config says disabled
    // Whether to ATTEMPT the streamed transport. Optimistic like `enabled`: a stale false here would cost the
    // paint-early effect, while a stale true only costs one 503 that falls back. `/config` reports the
    // backend's `streaming_enabled` so the kill switch doesn't have to be paid for on every message.
    const [streamingOn, setStreamingOn] = useState(true);
    // The paint-early handler bundle every interpret call site passes. NULL when streaming is off, which
    // `interpretUtterance` reads as "nothing is listening" and answers with the plain POST — so the switch
    // costs no request at all. onReset fires when a stream degrades mid-turn and the work is re-issued over
    // the POST, clearing partially painted text instead of leaving a truncated answer on screen.
    const streamHandlers = useMemo(
        () =>
            streamingOn
                ? {
                      onRoute: setStreamRoute,
                      onDelta: chunk => setStreamText(prev => prev + chunk),
                      onReset: () => setStreamText("")
                  }
                : null,
        [streamingOn]
    );
    const listRef = useRef(null);
    // Synchronous in-flight guard: `loading` is set via setState (async), so two rapid Enters/clicks in the
    // same tick can both pass the `loading` check and fire TWO identical interpret calls "at the same time".
    // A ref flips synchronously, so the second call is dropped before any network request goes out.
    const inFlightRef = useRef(false);
    // Conversation GENERATION: bumped whenever the thread on screen is replaced (Clear, resuming a history
    // session). A turn captures it when it starts; its continuation no-ops if the generation moved on, so a
    // slow interpret can't append the OLD thread's proposal into the conversation that replaced it.
    const convoGenRef = useRef(0);

    // Draggable panel: the user can grab the header and move Otto anywhere on screen. `dragOffset` is a
    // simple translate applied on top of the panel's default bottom-right anchor; the header is the drag
    // handle. The offset resets to zero every time the panel closes (see the effect below), so reopening
    // always brings Otto back to its home corner.
    const panelRef = useRef(null);
    const dragRef = useRef(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);

    const onHeaderPointerDown = e => {
        // Only the header background is a drag handle — presses on the action buttons (history/clear/close)
        // must still act as buttons, so bail if the press started on one. Left button / primary touch only.
        if (e.button !== 0 || (e.target.closest && e.target.closest("button"))) return;
        const rect = panelRef.current ? panelRef.current.getBoundingClientRect() : null;
        dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: dragOffset.x, baseY: dragOffset.y, rect };
        setDragging(true);
        if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onHeaderPointerMove = e => {
        const d = dragRef.current;
        if (!d) return;
        let dx = e.clientX - d.startX;
        let dy = e.clientY - d.startY;
        // Clamp so the whole panel stays on screen — it can never be dragged out of reach. `rect` is the
        // panel's box at drag start, so newLeft = rect.left + dx (the translate delta moves it by dx).
        if (d.rect) {
            const margin = 8;
            dx = Math.min(Math.max(dx, margin - d.rect.left), window.innerWidth - margin - d.rect.right);
            dy = Math.min(Math.max(dy, margin - d.rect.top), window.innerHeight - margin - d.rect.bottom);
        }
        setDragOffset({ x: d.baseX + dx, y: d.baseY + dy });
    };

    const endHeaderDrag = e => {
        if (!dragRef.current) return;
        dragRef.current = null;
        setDragging(false);
        if (e.currentTarget.releasePointerCapture && e.pointerId != null) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch (_) {
                /* pointer already released — ignore */
            }
        }
    };

    // Resizable panel: every edge and corner is a drag handle, the way a desktop window works. `panelSize.w`
    // is the CHAT column's width (the history sidebar is added on top when it's open — see panelWidth), and
    // `.h` the whole panel's height; the geometry lives in core/panelSize so this stays event plumbing.
    // Unlike the drag offset, the size is persisted: Otto remounts on every page navigation, and snapping
    // back to 380x600 each time would make an enlarged window feel broken.
    const resizeRef = useRef(null);
    const [panelSize, setPanelSize] = useState(() => clampSize(restored.panelSize || defaultSize(), false));
    const [resizing, setResizing] = useState(false);

    const onResizePointerDown = (e, dir) => {
        if (e.button !== 0) return;
        e.preventDefault(); // don't start a text selection under the handle
        const rect = panelRef.current ? panelRef.current.getBoundingClientRect() : null;
        resizeRef.current = {
            dir,
            startX: e.clientX,
            startY: e.clientY,
            base: { w: panelSize.w, h: panelSize.h, offX: dragOffset.x, offY: dragOffset.y, rect, historyOpen: showHistory }
        };
        setResizing(true);
        if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onResizePointerMove = e => {
        const r = resizeRef.current;
        if (!r) return;
        const next = resizeFrom(r.base, r.dir, e.clientX - r.startX, e.clientY - r.startY);
        setPanelSize({ w: next.w, h: next.h });
        // East/south grow away from the panel's bottom-right anchor, so they move it as well as size it.
        if (next.offX !== r.base.offX || next.offY !== r.base.offY) setDragOffset({ x: next.offX, y: next.offY });
    };

    const endResize = e => {
        if (!resizeRef.current) return;
        resizeRef.current = null;
        setResizing(false);
        if (e.currentTarget.releasePointerCapture && e.pointerId != null) {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch (_) {
                /* pointer already released — ignore */
            }
        }
    };

    // The header's one-click size toggle. Enlarged-ness is derived from the size itself rather than kept as
    // a flag, so an edge-drag away from the preset turns the button back into "Enlarge" with no extra state.
    const enlarged = isEnlarged(panelSize, showHistory);
    const toggleEnlarged = () => setPanelSize(enlarged ? defaultSize() : enlargedSize(showHistory));

    useEffect(() => {
        let active = true;
        if (open && !schema && !schemaError) {
            getFilterSchema()
                .then(s => active && setSchema(s))
                .catch(() => active && setSchemaError(true));
        }
        return () => {
            active = false;
        };
    }, [open, schema, schemaError]);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, loading, pendingText]);

    // Reset the drag position whenever the panel closes, so every reopen starts back in the home corner.
    useEffect(() => {
        if (!open) setDragOffset({ x: 0, y: 0 });
    }, [open]);

    // Persist the durable chat state on every change so a remount (navigation) or full reload restores it.
    // Clearing the conversation sets these to empty, which is what gets written here — so "clear" persists too.
    useEffect(() => {
        saveChat({ messages, sessionId, open, appliedStack, panelSize });
    }, [messages, sessionId, open, appliedStack, panelSize]);

    // Rollout gate: hide the launcher when the assistant is disabled for this user (server enforces too).
    useEffect(() => {
        let active = true;
        const userId = readUserId();
        getAssistantConfig(userId)
            .then(cfg => {
                if (!active || !cfg) return;
                if (cfg.enabled === false) setEnabled(false);
                // Absent (an older backend) leaves this true: the stream attempt then 404s and falls back.
                if (cfg.streaming === false) setStreamingOn(false);
            })
            .catch(() => {}); // fail open — the backend still enforces via 503
        return () => {
            active = false;
        };
    }, []);

    // Hold open the question a freshly built proposal asks, if any — the write side of the pending-question
    // protocol. Called wherever proposals surface (runQuery, the plan loop), so no clarify-shaped proposal
    // can forget its question: the NEXT message is then resolved against it (typed or via chips) instead of
    // re-entering the pipeline as a cold utterance. One-shot + TTL + page-bound (pendingQuestionStore).
    const holdOpenQuestion = useCallback(
        (proposal, utteranceText) => {
            const q = questionFromProposal(proposal, { page, utterance: utteranceText });
            if (q) saveQuestion(q);
        },
        [page]
    );

    // Run one utterance through the full proposal flow. Separated from submit() so a query typed before the
    // filter set finished loading can be queued and replayed here once it's ready (see the effect below).
    const runQuery = useCallback(
        async (text, opts = {}) => {
            if (!maps || inFlightRef.current) return; // drop a concurrent/duplicate run before it hits the network
            inFlightRef.current = true;
            const gen = convoGenRef.current; // the conversation this turn belongs to
            setMessages(prev => [...prev, { role: "user", text, ts: Date.now() }]);
            setLoading(true);
            setStreamRoute(null);
            setStreamText("");
            try {
                const userId = readUserId();
                // `messages` here is the prior turns (the new user message is queued via setMessages above
                // but not yet in this closure) — exactly the history to send alongside the current utterance.
                const history = buildHistory(messages);
                // App-context assembled at send time: the chart options (dashboard), the live table state
                // (table pages — read fresh so column changes are reflected), and the page/capability
                // MANIFEST (`pages`, record-only until the planner ships) that tells the backend what data
                // lives on which reachable page.
                const sendAppContext = {
                    ...(chartContext || {}),
                    ...(tableController ? { table: buildTableContext(tableController) } : {}),
                    pages: buildPageManifest({ page, navTargets })
                };
                const proposal = await buildProposal({
                    utterance: text,
                    projectId,
                    projectIds,
                    buildingIds,
                    dataView,
                    activeFilters,
                    maps,
                    userId,
                    sessionId,
                    history,
                    availableFields,
                    fieldsMeta,
                    appContext: sendAppContext,
                    navTargets,
                    tableController,
                    page,
                    candidateSource,
                    skipPageChoice: !!opts.skipPageChoice, // set when the user already picked "Filter the dashboard"
                    stream: streamHandlers
                });
                // The conversation was cleared/switched while this turn ran — its proposal belongs to the
                // old thread, so drop it (the finally below still releases the spinner + in-flight guard).
                if (gen !== convoGenRef.current) return;
                if (proposal.sessionId) setSessionId(proposal.sessionId);
                // If this proposal ASKS something (redirect/page/export choice, a table clarify), hold the
                // question open so the next typed reply resolves against it — same action as its buttons.
                holdOpenQuestion(proposal, text);
                setMessages(prev => [...prev, { role: "assistant", proposal }]);
            } catch (e) {
                if (gen === convoGenRef.current) {
                    setMessages(prev => [...prev, { role: "assistant", error: "Couldn’t interpret that — please try rephrasing." }]);
                }
            } finally {
                setLoading(false);
                setStreamRoute(null);
                setStreamText("");
                inFlightRef.current = false;
            }
        },
        [maps, projectId, projectIds, buildingIds, dataView, activeFilters, sessionId, messages, availableFields, fieldsMeta, chartContext, navTargets, page, tableController, holdOpenQuestion, streamHandlers]
    );

    // ---- Navigation + "go back" ---------------------------------------------------------------------
    // How Otto names the page it's mounted on ("FCA Dashboard", "Recommendations Table View" — the same
    // vocabulary every other Otto string uses, from the page manifest). This becomes the label of the back
    // affordance on the destination page. An unknown/unmapped mount contributes no name, and the row falls
    // back to "the previous page" rather than showing a raw key.
    const originLabel = useMemo(
        () => (isKnownPage(page) ? shortPageLabel({ key: page, label: pageLabelOf(page) }) : ""),
        [page]
    );

    // THE navigation chokepoint: every navigation Otto performs — nav card, cross-capability "open the … page"
    // pick, Q&A deep link / suggestion chip, plan navigate step — goes through here, so recording where the
    // user came from is done ONCE for all of them (a new nav call site gets the back affordance for free).
    // The snapshot is written to the store synchronously BEFORE the push: the push unmounts this component in
    // the same tick, so a React-state write wouldn't survive to the destination. Returns whatever the
    // container's executor returns (whether a push was dispatched), so callers' tracing is unchanged.
    const navigateTo = useCallback(
        (target, meta = {}) => {
            if (!onNavigate) return false;
            const entry = captureOrigin({
                label: originLabel,
                page,
                msgIdx: meta && typeof meta.msgIdx === "number" ? meta.msgIdx : null,
                target: target ? { key: target.key, label: shortPageLabel(target) } : null,
                trace: (meta && meta.trace) || null
            });
            if (entry) {
                pushNavBack(entry);
                setNavBack(entry);
            }
            return onNavigate(target);
        },
        [onNavigate, originLabel, page]
    );

    // Take the user BACK to the page Otto brought them from: pop the newest hop and push its snapshot through
    // the SAME executor a forward navigation uses (so the unsaved-changes guard applies and the origin's router
    // state + breadcrumb trail are restored — see navBackStore for why this is an explicit push, not a
    // history.goBack()). Newest-first like the filter Undo: after this the hop before it becomes the offered one.
    const goBackToOrigin = useCallback(() => {
        const entry = popNavBack();
        setNavBack(peekNavBack());
        if (!entry || !onNavigate) return;
        onNavigate({ key: entry.page, label: entry.label, url: entry.url, state: entry.state, bc: entry.bc || [] });
        traceExec(entry.trace || {}, "navigate.back", {
            output: { url: entry.url, page: entry.page || null, cameFrom: entry.target || null },
            metadata: { source: "nav_back" }
        });
    }, [onNavigate]);

    // Offer the hop only when the user is somewhere ELSE: an identical location means the push never happened
    // (a dirty-form / FCA-notice prompt deferred it) or they already went back another way. Checked on every
    // render so the row self-heals instead of offering a jump to the page you're standing on.
    // Also requires a navigation executor: a mount with no onNavigate can't move anyone, and an unclickable
    // "back" is worse than none (the mount-forgot-to-wire-it failure mode navTargets already taught us).
    const navBackEntry = onNavigate && navBack && navBack.url && !isCurrentLocation(navBack.url) ? navBack : null;
    // A hop fired from a chat card anchors its row under THAT card; one with no card (a plan's navigate step)
    // has no anchor, so it renders at the end of the conversation — where the eye already is after a jump.
    const navBackAnchored =
        !!navBackEntry && typeof navBackEntry.msgIdx === "number" && navBackEntry.msgIdx >= 0 && navBackEntry.msgIdx < messages.length;

    // ---- Table apply, with export progress ----------------------------------------------------------
    // The ONE way this container runs table changes: it calls the page's executor, and when an export is
    // among them it opens a ProgressRow bubble and settles it with whatever the export actually did. Every
    // caller goes through here — the cards, the plan loop, an answered question — so an export started from
    // any of them is announced the same way instead of each path inventing (or dropping) its own status.
    //
    // Why a message and not card-local state: an export is the one table op that takes real time (the report
    // backend builds the file), and it is usually the LAST step of a plan, i.e. it starts underneath a tall
    // plan card and its option buttons where a small grey "Working on it…" is invisible. The bubble appends
    // to the end of the thread, which auto-scrolls, so it's the thing the user is looking at.
    const progressSeqRef = useRef(0);
    const settleProgress = useCallback((id, text, status) => {
        setMessages(prev => prev.map(m => (m.progress && m.progress.id === id ? { ...m, progress: { ...m.progress, text, status } } : m)));
    }, []);
    const applyTableChanges = useCallback(
        changes => {
            const result = onTableApply(changes);
            const exportChange = (changes || []).find(c => c && c.action === "export");
            if (!exportChange) return result;
            progressSeqRef.current += 1;
            const id = `export-${progressSeqRef.current}`;
            setMessages(prev => [...prev, { progress: { id, text: exportProgressText(exportChange), status: "running" } }]);
            Promise.resolve(result)
                .then(r => {
                    // A page still on the fire-and-forget contract reports nothing — the executor's own
                    // default ("downloaded") already covers it, so the bubble never hangs on a spinner.
                    const outcome = (r && r.outcomes && r.outcomes[0]) || null;
                    const failed = !!outcome && outcome.status === "error";
                    settleProgress(id, exportOutcomeText(outcome) || EXPORT_OUTCOME_TEXT.downloaded, failed ? "error" : "done");
                })
                .catch(() => settleProgress(id, EXPORT_OUTCOME_TEXT.error, "error"));
            return result;
        },
        [onTableApply, settleProgress]
    );
    // Falsy without an executor: several call sites (the card's canApply, the plan loop) gate on this being
    // present, and a wrapper that silently no-ops would make them offer an Apply that does nothing.
    const tableApply = onTableApply ? applyTableChanges : undefined;

    // ---- Multi-task plan driver ---------------------------------------------------------------------
    // Executes a CONFIRMED plan step by step. Navigation steps drive the real router directly (the
    // pending-plan store carries the remaining steps across the unmount/remount); same-page steps replay
    // their sub_utterance through the NORMAL interpret pipeline (ground-on-page) and the grounded result
    // executes immediately (the plan was confirmed) — except filter steps, which render their card with
    // autoRun: a fully-confident resolution applies itself through the live feasibility gate, anything
    // ambiguous PAUSES the plan until the user picks + applies (applyFilters resumes it). The cursor
    // advances BEFORE each dispatch (at-most-once): a reload mid-step drops that step, never doubles it.
    const planActiveRef = useRef(false);
    const runPlanLoopRef = useRef(null);
    // The last step this loop actually DISPATCHED, `{ stepNo, total }`. Needed because `loadPlan()` returns
    // null for two opposite outcomes — the entry is consumed when the final step dispatches, and it also
    // self-destructs when the TTL lapses — so without this the loop cannot tell "finished" from "dropped".
    const planProgressRef = useRef(null);
    const runPlanLoop = useCallback(async () => {
        if (!maps) return;
        // A friendly, table-view-aware name for a step's target page: the reachable target's name
        // ("Recommendations Table View" / "the Regions page" via pageReference) when we have it, falling back
        // to the raw key only when the page isn't in this mount's reachable set.
        const describeStepPage = tp => {
            const t = (navTargets || []).find(x => x && x.key === tp);
            return t ? pageReference(t) : `${tp} page`;
        };
        for (let guard = 0; guard < 8; guard++) {
            const pending = loadPlan();
            if (!pending) {
                if (planActiveRef.current) {
                    planActiveRef.current = false;
                    // FINISHED or EXPIRED? Reporting "complete" unconditionally claimed success for a plan
                    // that was dropped mid-flight: a 3-step plan paused on an ambiguous filter, its TTL
                    // lapsed while the user read Otto's own clarification, and the resume printed
                    // "✅ Plan complete." with the export step never run (trace 4c9d3029 / FTO-7). Steps
                    // remaining after the last dispatched one means it did NOT finish.
                    const progress = planProgressRef.current;
                    const unrun = (progress && progress.remaining) || [];
                    planProgressRef.current = null;
                    // Name what was dropped, and don't promise an automatic resume — the entry is gone, so
                    // the user has to ask for the rest.
                    setMessages(prev => [
                        ...prev,
                        unrun.length
                            ? {
                                  system: `Sorry — the plan timed out before ${unrun.length > 1 ? "these steps" : "this step"} could run: ${unrun.join(", ")}. Ask me and I'll do it now.`
                              }
                            : { system: "✅ Plan complete." }
                    ]);
                }
                return;
            }
            planActiveRef.current = true;
            const step = pending.steps[pending.cursor];
            const stepNo = pending.cursor + 1;
            const total = pending.steps.length;
            // Carry the labels of the steps QUEUED BEHIND this one, so an expiry can say exactly what got
            // dropped instead of just counting.
            planProgressRef.current = {
                stepNo,
                total,
                remaining: pending.steps.slice(pending.cursor + 1).map(s => s.label || s.sub_utterance)
            };

            // NAVIGATE: deterministic — the destination IS the step's target_page; drive the real router.
            if (step.capability === "navigate") {
                advancePlanCursor();
                const target = (navTargets || []).find(t => t && t.key === step.target_page);
                if (!target || !onNavigate) {
                    planActiveRef.current = false;
                    clearPlan();
                    setMessages(prev => [...prev, { system: `Plan stopped — I can't open the ${describeStepPage(step.target_page)} from here.` }]);
                    return;
                }
                setMessages(prev => [...prev, { system: `▶ Step ${stepNo}/${total}: ${step.label}…` }]);
                // Through navigateTo (not onNavigate directly) so a plan hop records the origin too — the back
                // row then shows at the end of the chat, since a step line is not a card to anchor under.
                const pushed = navigateTo(target, { trace: { requestId: pending.planId, sessionId } });
                // A plan's navigate step has no per-step interpret trace (it's deterministic), so anchor its
                // execution span to the PLAN's turn (planId) — the tracing waterfall then shows the plan
                // driving each hop.
                traceExec(
                    { requestId: pending.planId, sessionId },
                    "navigate.execute",
                    {
                        output: { target: { key: target.key, label: target.label, url: target.url }, pushed: pushed !== false, step: stepNo, total },
                        metadata: { source: "plan" }
                    }
                );
                return; // the page unloads; the destination mount resumes the plan from the store
            }

            // A non-navigation step for another page with no navigation before it (planner error) — stop cleanly.
            if (step.target_page !== page) {
                planActiveRef.current = false;
                clearPlan();
                setMessages(prev => [...prev, { system: `Plan stopped — “${step.label}” belongs to the ${describeStepPage(step.target_page)}.` }]);
                return;
            }

            advancePlanCursor();
            setMessages(prev => [...prev, { system: `▶ Step ${stepNo}/${total}: ${step.label}…` }]);
            setLoading(true);
            setStreamRoute(null);
            setStreamText("");
            inFlightRef.current = true;
            let proposal = null;
            try {
                proposal = await buildProposal({
                    utterance: step.sub_utterance,
                    projectId,
                    projectIds,
                    buildingIds,
                    dataView,
                    activeFilters,
                    maps,
                    userId: readUserId(),
                    sessionId,
                    history: [], // steps are self-contained; prior chat turns would only confuse the capture
                    availableFields,
                    fieldsMeta,
                    appContext: {
                        ...(chartContext || {}),
                        ...(tableController ? { table: buildTableContext(tableController) } : {}),
                        pages: buildPageManifest({ page, navTargets }),
                        plan: { plan_id: pending.planId, step: stepNo, total } // trace linkage for tracing_app
                    },
                    navTargets,
                    tableController,
                    page,
                    candidateSource,
                    // A plan is an already-decided sequence — a filter step that runs ON the dashboard must not
                    // re-open the page-choice card (it would loop, and the page was already chosen). See onPageChoice.
                    skipPageChoice: true,
                    // ...and the step's CAPABILITY pins its route: the backend classifier is bypassed
                    // (route_hint), so replaying "filter to Florida and export the table" can never be
                    // re-read as a brand-new multi_task plan mid-plan. The capability was what the user
                    // confirmed when they ran the plan.
                    routeHint: ROUTE_HINT_BY_CAPABILITY[step.capability] || null,
                    // Same paint-early hints as a typed message — a step that answers a question writes it
                    // out as it goes rather than sitting on the step's spinner.
                    stream: streamHandlers
                });
            } catch (e) {
                setLoading(false);
                setStreamRoute(null);
                setStreamText("");
                inFlightRef.current = false;
                planActiveRef.current = false;
                clearPlan();
                setMessages(prev => [...prev, { system: `Plan stopped — step ${stepNo} (“${step.label}”) failed.` }]);
                return;
            }
            setLoading(false);
            setStreamRoute(null);
            setStreamText("");
            inFlightRef.current = false;
            if (proposal.sessionId) setSessionId(proposal.sessionId);

            // A ROUTE-CHANGING shape coming back from a step replay — a nested plan, a page choice, a
            // capability redirect — means the step was re-routed instead of executed: the router changing
            // its mind mid-plan (the reported "step 2 became a new 3-step plan" failure). The route hint
            // above prevents this against a current backend; against an OLDER deployed backend (which
            // ignores the hint) stop honestly rather than render a second plan mid-plan.
            if (proposal.plan || proposal.pageChoice || proposal.redirectChoice) {
                planActiveRef.current = false;
                clearPlan();
                traceExec({ requestId: pending.planId, sessionId }, "plan.step_reroute", {
                    status: "error",
                    output: { step: stepNo, capability: step.capability, came_back_as: proposal.plan ? "plan" : proposal.pageChoice ? "page_choice" : "redirect" }
                });
                setMessages(prev => [
                    ...prev,
                    { system: `Plan stopped — I couldn't run step ${stepNo} (“${step.label}”) here. Try asking for it directly.` }
                ]);
                return;
            }

            // An export CHOICE is a decision the user still OWES — the plan's Run confirmed "export, over
            // there", never WHICH export. This step used to silently collapse to the simple export and print
            // the unanswered question as if it were a receipt ("✓ I can export this as a Simple Excel file,
            // or open the export settings… Which would you like" → "✅ Plan complete."), so a plan reaching an
            // export produced a file the user never chose. Pause and ask instead — the same rule the loop
            // applies to every other outstanding decision below; the card's buttons carry their own changes,
            // so the pick is the confirmation and no second card appears.
            //
            // The plan ends here either way: the cursor is already past this step, and there is no resume
            // hook on a table apply (unlike a filter apply). With the export LAST — every capability-redirect
            // plan, and the only shape that reaches this in practice — that is exactly right: the user's pick
            // IS the final act. Anything queued after it is called out rather than dropped in silence.
            if (isExportChoice(proposal)) {
                planActiveRef.current = false;
                clearPlan();
                holdOpenQuestion(proposal, step.sub_utterance); // a typed "simple excel" answers this pause too
                const skipped = total - stepNo;
                setMessages(prev => [
                    ...prev,
                    { role: "assistant", proposal },
                    ...(skipped > 0
                        ? [{ system: `Waiting on your export choice — the ${skipped} step${skipped > 1 ? "s" : ""} after this one won't run on their own.` }]
                        : [])
                ]);
                return;
            }
            // Grounded, non-interactive results execute immediately — the plan card was the confirmation.
            if (proposal.table && !proposal.needsClarification && (proposal.changes || []).length && tableApply) {
                tableApply(proposal.changes);
                traceExec(proposal, "table_command.execute", { output: { changes: proposal.changes || [], step: stepNo }, metadata: { source: "plan" } });
                setMessages(prev => [...prev, { system: `✓ ${(proposal.message || step.label).replace(/\?$/, "")}` }]);
                continue;
            }
            if (proposal.chart && !proposal.needsClarification && (proposal.changes || []).length && onChartApply) {
                onChartApply(proposal.changes);
                traceExec(proposal, "chart_command.execute", { output: { changes: proposal.changes || [], step: stepNo }, metadata: { source: "plan" } });
                setMessages(prev => [...prev, { system: `✓ ${(proposal.message || step.label).replace(/\?$/, "")}` }]);
                continue;
            }
            if (proposal.reset && onReset) {
                onReset();
                traceExec(proposal, "dashboard_reset.execute", { output: { step: stepNo }, metadata: { source: "plan" } });
                setMessages(prev => [...prev, { system: "✓ Dashboard reset." }]);
                continue;
            }
            // A filter step (or a clarify from any capability): render the real card. autoRun lets a
            // fully-confident filter apply itself; anything else pauses here for the user, and the plan
            // resumes on apply. Non-executable strays (smalltalk / a nested plan / removal) stop the plan.
            // A clarify raised MID-PLAN holds its question open like any other — this is the "export Florida
            // buildings" → "Which Region did you mean?" → "all of them" path: the answer must resolve against
            // the question, not re-enter the pipeline cold.
            if (proposal.intent || proposal.needsClarification) {
                holdOpenQuestion(proposal, step.sub_utterance);
                // Only announce queued steps when this card will actually WAIT for the user. A filter
                // proposal signals that per result (`decision` confirm/clarify) — the same predicate the
                // card uses for its own refine hint — NOT via a top-level flag, and it carries
                // `proposal.intent` either way, so gating on `intent`/`needsClarification` printed nothing
                // on the real "compliance issues" pause (verified live, twice). A fully-confident card
                // autoRuns, applies itself and the plan continues unattended, so that case stays quiet.
                const asked =
                    ((proposal.resolution && proposal.resolution.results) || []).some(
                        r => r.decision === "confirm" || r.decision === "clarify"
                    ) ||
                    !!proposal.needsClarification ||
                    !!proposal.pendingQuestion;
                // Control has left the plan: it cannot proceed until the user picks and applies, so stop
                // running the inactivity clock against them. Restarting it at the moment Otto asked (FTO-7)
                // still expired mid-deliberation — 6m45s spent on a card offering four identically-named
                // options killed the navigate step of trace 6d475bbd. A card that autoRuns instead keeps
                // the plan running, so it only gets a touch. Either way the 30-minute ceiling stands, and
                // `advancePlanCursor` unblocks the plan the moment it resumes. Also say out loud that steps
                // are queued — the same courtesy the export-choice pause above already extends.
                if (asked) blockPlan();
                else touchPlan();
                const queued = total - stepNo;
                setMessages(prev => [
                    ...prev,
                    { role: "assistant", proposal, autoRun: !!proposal.intent },
                    ...(queued > 0 && asked
                        ? [{ system: `The ${queued} step${queued > 1 ? "s" : ""} after this one will continue once you apply.` }]
                        : [])
                ]);
                return;
            }
            planActiveRef.current = false;
            clearPlan();
            setMessages(prev => [...prev, { role: "assistant", proposal }, { system: `Plan stopped after step ${stepNo}.` }]);
            return;
        }
    }, [maps, projectId, projectIds, buildingIds, dataView, activeFilters, sessionId, availableFields, fieldsMeta, chartContext, tableController, navTargets, page, onNavigate, navigateTo, tableApply, onChartApply, onReset, holdOpenQuestion, streamHandlers]);
    runPlanLoopRef.current = runPlanLoop;

    // Run is the plan's ONE confirmation (locked rule): persist the steps, then drive them.
    const startPlan = useCallback(proposal => {
        if (!proposal || !(proposal.steps || []).length) return;
        // Committing to a plan supersedes any question Otto was holding open — including the redirect
        // question whose own BUTTON leads here, which must not stay answerable after it's been answered.
        clearQuestion();
        savePlan({ planId: proposal.planId || proposal.requestId, steps: proposal.steps });
        planActiveRef.current = true;
        if (runPlanLoopRef.current) runPlanLoopRef.current();
    }, []);

    // A pending plan whose next step runs on THIS page auto-opens Otto: the schema/maps load only while
    // the panel is open, and the resume gate below needs them — without this, a cross-page plan whose
    // panel didn't survive the navigation would silently wait until the TTL expired.
    useEffect(() => {
        const pending = loadPlan();
        if (!pending) return;
        const next = pending.steps[pending.cursor];
        if (next && (next.target_page === page || next.capability === "navigate")) setOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resume a pending plan on mount — the destination page after a navigation step. Waits for the same
    // readiness gate as a typed query (maps + filters), then continues if the next step belongs to THIS
    // page. One-shot per mount; a plan for some other page is left alone (the TTL cleans it up).
    const planResumeRef = useRef(false);
    useEffect(() => {
        if (!maps || !filtersReady || planResumeRef.current) return;
        const pending = loadPlan();
        if (!pending) return;
        // A plan BLOCKED on a question resumes when that question is ANSWERED (applyFilters), never because
        // the page happened to mount again: a reload is not an answer, and auto-continuing would skip the
        // pause and run the next step — a surprise navigation — while the user's card still sits unanswered.
        // Blocked plans now outlive the inactivity window (FTO-8), so this is the guard that keeps that from
        // widening the reload hazard along with it.
        if (pending.awaitingUserAt) return;
        const next = pending.steps[pending.cursor];
        if (!next) return;
        if (next.capability !== "navigate" && next.target_page !== page) return;
        planResumeRef.current = true;
        planActiveRef.current = true;
        setMessages(prev => [...prev, { system: `Continuing the plan (step ${pending.cursor + 1}/${pending.steps.length})…` }]);
        if (runPlanLoopRef.current) runPlanLoopRef.current();
    }, [maps, filtersReady, page]);

    // ---- Pending-question resolution ----------------------------------------------------------------
    // RUN what an answered question resolves to. `q` is a stored pending-question entry (typed path) or one
    // freshly derived from the proposal (chip path — questionFromProposal); `picks` are the selected option
    // objects. The options carry everything their action needs (steps / changes / ids), so typed answers,
    // chips, and the cards' own buttons can only ever do the same thing. Returns false when the kind (or a
    // missing handler) can't act — the caller then degrades gracefully instead of dropping the message.
    const onPageChoiceRef = useRef(null); // onPageChoice is defined below and depends on submitText — break the cycle
    const runQuestionResolution = useCallback(
        (q, picks, source) => {
            traceExec({ requestId: q.questionId, sessionId }, "clarify_answer.execute", {
                output: { kind: q.kind, picks: picks.map(p => (p.id != null ? p.id : p.label)), source }
            });
            // Capability redirect: the pick is a page whose option carries the whole navigate+replay plan.
            if (q.kind === "redirect_choice") {
                const pick = picks[0];
                if (!pick || !(pick.steps || []).length) return false;
                startPlan({ planId: `${q.questionId || "redirect"}-${pick.id}`, steps: pick.steps });
                return true;
            }
            // Page choice: run the same flow the card's button would (onPageChoice reads target/utterance).
            if (q.kind === "page_choice") {
                const pick = picks[0];
                if (!pick || !onPageChoiceRef.current || !(q.payload && q.payload.target)) return false;
                onPageChoiceRef.current(pick.id, { target: q.payload.target, utterance: q.utterance, requestId: q.questionId });
                return true;
            }
            // RUN grounded table changes now — the question+answer pair was the confirmation. An export among
            // them reports its real outcome (downloaded vs Export History) through applyTableChanges' bubble.
            const applyResolvedChanges = changes => {
                if (!(changes || []).length || !tableApply) return false;
                setMessages(prev => [...prev, { system: `✓ ${changes.map(describeTableChange).join(", and ")}` }]);
                tableApply(changes);
                traceExec({ requestId: q.questionId, sessionId }, "table_command.execute", {
                    output: { changes },
                    metadata: { source: `question_${source}` }
                });
                return true;
            };
            // Re-run the ORIGINAL raw changes (with the answer substituted in) through the normal proposal
            // builder — so sibling changes the question interrupted ("…and export it") resolve too, and a
            // legitimately-still-open decision (the next clarify, an export choice) renders its own card
            // and holds ITS question open. `directApply` runs a fully-resolved result immediately (the
            // answer was the confirmation); without it the rebuilt proposal keeps its own confirm rules.
            const rebuildTable = (changes, directApply) => {
                setLoading(true);
                buildTableProposal(
                    { request_id: q.questionId, session_id: sessionId, intent_kind: "table_command", table: { changes } },
                    { utterance: q.utterance, appContext: { table: buildTableContext(tableController) }, tableController }
                )
                    .then(p => {
                        if (directApply && p.table && !p.needsClarification && (p.changes || []).length) {
                            applyResolvedChanges(p.changes);
                            return;
                        }
                        holdOpenQuestion(p, q.utterance);
                        setMessages(prev => [...prev, { role: "assistant", proposal: p }]);
                    })
                    .catch(() => {
                        // The user answered a question — a failed rebuild must say so, not just stop the
                        // spinner with no card. Same reply as a failed cold interpret (runQuery's catch).
                        setMessages(prev => [...prev, { role: "assistant", error: "Couldn’t interpret that — please try rephrasing." }]);
                    })
                    .then(() => setLoading(false));
                return true;
            };
            // A single option carrying the exact changes it runs (formats, page sizes, export choices).
            if (q.kind === "table_changes") {
                return applyResolvedChanges((picks[0] && picks[0].changes) || []);
            }
            // Column VALUES (multi-pick — "all of them" selects every candidate): substitute the picked
            // names into the change that raised the question and re-run the whole request.
            if (q.kind === "table_values") {
                const raw = (q.payload && q.payload.changes) || [];
                const idx = q.payload && typeof q.payload.failedIndex === "number" ? q.payload.failedIndex : -1;
                const values = picks.map(p => p.label);
                if (raw[idx]) {
                    return rebuildTable(raw.map((ch, i) => (i === idx ? { ...ch, values, value: null } : ch)), true);
                }
                // No raw context (a stored question predating it) — apply the answered filter alone.
                return applyResolvedChanges([{ action: "column_filter", column: q.payload && q.payload.column, mode: "options", values }]);
            }
            // Column pick: substitute the picked column for the phrase that failed and re-run — "Construction
            // Cost" re-runs the whole "sort by cost" request (which may raise the NEXT question, e.g. values).
            if (q.kind === "table_column") {
                const pick = picks[0];
                const raw = (q.payload && q.payload.changes) || [];
                if (!pick || !raw.length) return false;
                const failed = (q.payload && q.payload.failedColumn) || "";
                return rebuildTable(raw.map(ch => (ch && (ch.column || "") === failed ? { ...ch, column: pick.label } : ch)), false);
            }
            // Browse-card picks (FTO-10): "energy", "the first one", "energy and life safety" — each option
            // carries its full match, so the apply flows through buildApplyPayload exactly like tapping the
            // pills. The typed answer IS the confirmation (same rule as table_changes above).
            if (q.kind === "field_options") {
                const matches = picks.map(p => p && p.match).filter(Boolean);
                if (!matches.length || !onApply || !q.payload) return false;
                const ref = q.payload.ref || "b:0";
                const payload = buildApplyPayload({
                    intent: { common_filters: [], clears: [], wildcard_filters: [] },
                    resolution: { results: [{ ref, suggestions: matches }] },
                    selections: { [ref]: matches },
                    maps
                });
                setMessages(prev => [...prev, { system: `✓ Filtering ${q.payload.label}: ${matches.map(m => m.name).join(", ")}` }]);
                onApply(payload, { requestId: q.questionId, sessionId });
                // A plan paused on the browse card resumes exactly as it would on the card's own Apply.
                if (loadPlan() || planActiveRef.current) {
                    setTimeout(() => runPlanLoopRef.current && runPlanLoopRef.current(), 350);
                }
                return true;
            }
            return false;
        },
        [sessionId, startPlan, tableApply, tableController, holdOpenQuestion, onApply, maps]
    );

    // Is this message the TYPED answer to the question Otto is holding open? Deterministic resolution:
    // page-name grounding for redirect questions (label + nav synonyms), then the shared answer matcher —
    // quantifiers ("all of them"), dismissals ("never mind"), affirmations on one-option questions ("yes"),
    // ordinals ("the first one", "1 and 3"), and option names (fuzzy, tiered). Anything it can't match
    // falls through to the normal pipeline — it may simply be a new request.
    //
    // The question is ONE-SHOT: cleared here whether or not it matched, so a reply that changed the subject
    // can't be answered by it two turns later.
    const answerPendingQuestion = useCallback(
        text => {
            const pending = loadQuestion(page);
            if (!pending) return false;
            clearQuestion();
            let picks = null;
            // Redirect questions get the richer page-name grounding first ("recs" via nav synonyms).
            if (pending.kind === "redirect_choice") {
                const choice = resolveRedirectAnswer(text, pending, navTargets);
                if (choice) picks = [choice];
            }
            if (!picks) {
                const m = matchAnswer(text, pending.options, { multi: pending.multi });
                if (m && m.type === "none") {
                    // An explicit dismissal is an answer too: acknowledge and stand down — never re-ask.
                    setMessages(prev => [
                        ...prev,
                        { role: "user", text, ts: Date.now() },
                        { role: "assistant", proposal: { clarify: true, message: "Okay — I've dropped that. What would you like to do instead?" } }
                    ]);
                    traceExec({ requestId: pending.questionId, sessionId }, "clarify_answer.execute", {
                        output: { kind: pending.kind, picks: [], source: "typed", dismissed: true }
                    });
                    return true;
                }
                if (m) picks = m.picks;
            }
            if (!picks || !picks.length) {
                // FTO-10 narrowing: on a browse question, a word that isn't a pick is a SEARCH within the
                // field ("pump" on Sub System's 218 options). Synchronous by design — the full vocabulary
                // rides in payload.options_all — so this decides instantly and a word matching NOTHING
                // falls through to the cold pipeline like any other new request ("go to buildings" while
                // a browse card is open still navigates).
                // FTO-12 threshold answers: "over 50k" typed under a threshold card composes
                // "<field label> over 50k" and re-enters the pipeline — the interpreter owns operator and
                // magnitude parsing, so no NL is duplicated here. With SEVERAL candidate fields ("cost"
                // named Construction Cost + Project Cost + …), the reply must name one ("construction
                // over 1m"); a number with no field named falls through to the cold pipeline rather than
                // guessing which field the user meant. runQuery posts the COMPOSED text as the user
                // bubble — transparent about how the reply was read.
                if (pending.kind === "field_threshold" && pending.payload && /\d/.test(text)) {
                    const fields = pending.payload.fields || [];
                    const textNorm = ` ${String(text).toLowerCase()} `;
                    const named = fields.filter(f =>
                        String(f.label || "")
                            .toLowerCase()
                            .split(/[^a-z0-9]+/)
                            .filter(t => t.length >= 3)
                            .some(t => textNorm.includes(t))
                    );
                    const target = fields.length === 1 ? fields[0] : named.length === 1 ? named[0] : null;
                    if (target) {
                        const already = String(text).toLowerCase().includes(String(target.label).toLowerCase());
                        runQuery(already ? text : `${target.label} ${text}`);
                        traceExec({ requestId: pending.questionId, sessionId }, "field_mention.execute", {
                            output: { action: "threshold_reply", field: target.field, composed: !already }
                        });
                        return true;
                    }
                    return false;
                }
                if (pending.kind === "field_options" && pending.payload && (pending.payload.options_all || []).length) {
                    const { hits, exact } = narrowOptions(pending.payload.options_all, text);
                    if (hits.length) {
                        setMessages(prev => [...prev, { role: "user", text, ts: Date.now() }]);
                        // An exact, unique hit is a pick, not a narrowing ("water heater" typed in full):
                        // apply it directly instead of asking again.
                        const metaLike = { id: pending.payload.field, label: pending.payload.label, value_kind: pending.payload.value_kind };
                        if (exact) {
                            const match = { field: metaLike.id, id: hits[0].id, name: hits[0].name, value_kind: metaLike.value_kind };
                            return runQuestionResolution({ ...pending, kind: "field_options" }, [{ id: String(match.id), label: match.name, match }], "typed");
                        }
                        // Narrowed browse card: same shape the orchestrator gate synthesizes, options_all
                        // kept FULL so the next word searches the whole vocabulary again. Re-holding the
                        // question is the ask→answer→ask loop (precedent: rebuildTable above).
                        const narrowed = buildBrowseResult(metaLike, hits, { ref: pending.payload.ref || "b:0", phrase: pending.payload.phrase, narrowWord: text });
                        narrowed.browse.total = pending.payload.total;
                        narrowed.browse.options_all = pending.payload.options_all;
                        const proposal = {
                            intent: { needs_clarification: true, common_filters: [], ambiguous_terms: [], wildcard_filters: [], clears: [] },
                            resolution: { results: [narrowed] },
                            requestId: pending.questionId,
                            sessionId,
                            utterance: pending.utterance
                        };
                        holdOpenQuestion(proposal, pending.utterance);
                        setMessages(prev => [...prev, { role: "assistant", proposal }]);
                        traceExec({ requestId: pending.questionId, sessionId }, "field_mention.execute", {
                            output: { action: "narrow", field: pending.payload.field, word: text, hits: hits.length }
                        });
                        return true;
                    }
                }
                return false;
            }
            setMessages(prev => [...prev, { role: "user", text, ts: Date.now() }]);
            if (!runQuestionResolution(pending, picks, "typed")) {
                // Matched but unrunnable (a handler this page lacks) — say so rather than going silent; the
                // user bubble is already posted, so the message must not ALSO run through the cold pipeline.
                setMessages(prev => [...prev, { role: "assistant", proposal: { clarify: true, message: "I couldn't run that here — try the buttons above, or rephrase what you'd like." } }]);
            }
            return true;
        },
        [page, navTargets, sessionId, runQuestionResolution, holdOpenQuestion, runQuery]
    );

    // The CHIP path — a tap on a clarify card's option(s). Same resolution as typing, minus the matching:
    // the card hands over the exact options picked. The open question is superseded either way (one-shot).
    const onQuestionAnswer = useCallback(
        (proposal, picks) => {
            const q = questionFromProposal(proposal, { page, utterance: proposal.utterance });
            if (!q || !(picks || []).length) return;
            clearQuestion();
            runQuestionResolution(q, picks, "chip");
        },
        [page, runQuestionResolution]
    );

    // Send an utterance through the proposal flow. Shared by the text box (submit) and the "Click for
    // Examples" modal, so a tapped example behaves exactly like the user typing it and hitting Send.
    const submitText = useCallback(
        (raw, opts = {}) => {
            const text = (raw || "").trim();
            if (!text || loading || pendingText) return;
            // Wait for the schema/maps AND the dashboard's filter set to finish loading before resolving.
            // Otherwise the send would silently no-op (maps still null — e.g. on a page where the schema is
            // fetched only when Otto opens, so a fast first message races it) or a late-loading field (e.g. the
            // priority band's "Compliance") wouldn't be a candidate yet and gets missed. Queue the query; the
            // effect below replays it the moment BOTH are ready.
            if (!maps || !filtersReady) {
                setPendingText(text);
                return;
            }
            // An answer to Otto's open question short-circuits the pipeline (it already knows what to do).
            // Only text the USER just produced can be an answer: a PROGRAMMATIC re-submit (a page-choice
            // pick re-running the original query, a tapped example) is a fresh request that happens to flow
            // through here, and one that names a page could otherwise hijack a question left open by an
            // earlier turn. Those still END the question — any new request does — they just can't answer it.
            // Plan steps don't come through here at all (runPlanLoop calls buildProposal directly).
            if (opts.skipPendingClarify) clearQuestion();
            else if (answerPendingQuestion(text)) return;
            runQuery(text, opts);
        },
        [loading, pendingText, maps, filtersReady, runQuery, answerPendingQuestion]
    );

    // Page-choice answer — the pick IS the confirmation, so each choice runs its flow directly (no 2nd card),
    // reusing the plan executor:
    //   here           -> re-run the SAME query on the dashboard, skipping the prompt this time.
    //   open-local     -> navigate to the page, then filter it LOCALLY (its own filters / table column filter).
    //   open-dashboard -> filter the DASHBOARD (persists + propagates), THEN navigate — the page inherits it.
    // The plan's steps replay through the normal per-capability pipeline on their execution page (ground-on-page).
    const onPageChoice = useCallback(
        (kind, proposal) => {
            const target = proposal.target;
            if (kind === "here") {
                submitText(proposal.utterance, { skipPageChoice: true, skipPendingClarify: true });
                return;
            }
            const navStep = {
                capability: "navigate",
                // sub_utterance is the INTERNAL phrase re-grounded by the nav pipeline — keep it "the … page".
                sub_utterance: `go to the ${target.label} page`,
                target_page: target.key,
                label: pageActionLabel("Open", target)
            };
            const localFilterStep = {
                capability: "filters",
                sub_utterance: proposal.utterance,
                target_page: target.key,
                label: `Filter: ${proposal.utterance}`
            };
            const dashboardFilterStep = {
                capability: "filters",
                sub_utterance: proposal.utterance,
                target_page: "dashboard",
                label: `Filter the dashboard: ${proposal.utterance}`
            };
            // open-dashboard: filter the dashboard FIRST (so it's applied + in redux before navigating), then
            // go to the page — which inherits the dashboard filter via propagation. open-local: navigate then
            // filter the page's own surface.
            const steps = kind === "open-dashboard" ? [dashboardFilterStep, navStep] : [navStep, localFilterStep];
            // A residual ACTION detected in the utterance ("a report of my florida recommendations" that the
            // classifier read as a plain filter) runs AFTER the filter, on the destination page — so picking
            // where to filter can't drop the export half of the goal. The sub_utterance is deterministic
            // ("export the table"), not the original text, so the table interpreter can't re-capture the
            // filter half as a spurious row filter. On Recommendations the export step then offers the
            // page's real export options (simple / variants / settings) exactly like a typed export would.
            if (proposal.action === "export") {
                steps.push({
                    capability: "table_view",
                    sub_utterance: "export the table",
                    target_page: target.key,
                    label: "Export the table"
                });
            }
            startPlan({ planId: `pagechoice-${proposal.requestId || Date.now()}`, steps });
        },
        [submitText, startPlan]
    );
    // Typed answers to a page-choice question resolve through runQuestionResolution, which is defined
    // BEFORE onPageChoice (it feeds submitText) — the ref breaks that cycle.
    onPageChoiceRef.current = onPageChoice;

    // A cross-capability filter pick on an ENTITY noun ("recommendations") can't run a literal filter, so it
    // posts this open clarifying question as a plain Otto bubble. The user's typed answer then goes through
    // submitText like any other message, grounded against the real fields — no simulated "recommendations" query.
    const onClarify = useCallback(message => {
        setMessages(prev => [...prev, { role: "assistant", proposal: { clarify: true, message } }]);
    }, []);

    const submit = useCallback(() => {
        // Mirror submitText's in-flight guard BEFORE wiping the input: submitText drops a send while a
        // turn is running or queued, so clearing first silently discarded whatever was typed (Enter is
        // not disabled the way the Send button is). The queued-behind-schema path still clears — there
        // pendingText is set by submitText itself, after this check.
        if (!input.trim() || loading || pendingText) return;
        setInput("");
        submitText(input);
    }, [input, loading, pendingText, submitText]);

    // Fire a pre-baked example: close whichever modal is open, then run it like a normal submit.
    const pickExample = useCallback(
        text => {
            setActiveModal(null);
            submitText(text, { skipPendingClarify: true }); // a canned prompt is a new request, not an answer
        },
        [submitText]
    );

    // Open a prompt modal from a collapsed state (every group back to its first EXAMPLES_STEP).
    const openModal = useCallback(name => {
        setExampleCounts({});
        setActiveModal(name);
    }, []);

    // Reveal `step` more prompts within a single group (defaults to EXAMPLES_STEP).
    const loadMoreExamples = useCallback(
        (title, step = EXAMPLES_STEP) => setExampleCounts(c => ({ ...c, [title]: (c[title] || step) + step })),
        []
    );

    // Replay a queued query as soon as the filter set is ready. Goes through the same open-question check as
    // a live send — a message typed while the schema was still loading (a reload mid-question) is just as
    // likely to be the answer to it.
    useEffect(() => {
        if (maps && filtersReady && pendingText && !loading) {
            const text = pendingText;
            setPendingText(null);
            if (!answerPendingQuestion(text)) runQuery(text);
        }
    }, [maps, filtersReady, pendingText, loading, runQuery, answerPendingQuestion]);

    // A queued query can never replay once the schema fetch failed (the replay above waits on maps, and the
    // schema effect doesn't retry) — flush it as real bubbles instead of leaving the queued message spinning
    // forever with Send disabled behind it.
    useEffect(() => {
        if (schemaError && pendingText) {
            const text = pendingText;
            setPendingText(null);
            setMessages(prev => [
                ...prev,
                { role: "user", text, ts: Date.now() },
                { role: "assistant", error: "Couldn’t reach the filter assistant service." }
            ]);
        }
    }, [schemaError, pendingText]);

    const onKeyDown = e => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    // Grow the composer to fit what's typed (CSS caps it, then it scrolls) so the box starts as one line
    // and a long utterance stays readable. Keyed on `input` so the programmatic paths resize too — sending
    // clears it back to one line, and an answer typed for Otto's question is measured the same way.
    useLayoutEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto"; // reset first so the box can also SHRINK back
        if (el.scrollHeight) el.style.height = `${el.scrollHeight}px`;
    }, [input]);

    // Clear the chat back to a blank conversation. The welcome bubble is rendered outside `messages`, so
    // it stays put — the user is left with just Otto's opening message. Drop the sessionId too so the next
    // turn starts a fresh server-side conversation rather than resuming the one we just wiped locally. The
    // emptied state is persisted by the effect above, so this is the ONE action that wipes the saved chat.
    const clearConversation = useCallback(() => {
        setMessages([]);
        setSessionId(null);
        setPendingText(null);
        setAppliedStack([]);
        clearPlan(); // Clear also abandons any pending multi-step plan
        clearQuestion(); // ...and any question Otto was still holding open (its card is being wiped too)
        // ...and the recorded navigation hops: their rows are anchored to the messages just wiped, so keeping
        // them would offer a "back" with nothing in the chat explaining where it came from.
        clearNavBack();
        setNavBack(null);
        // ...and the record of what the user decided in it. Those decisions belong to the conversation being
        // wiped; carrying them over would have Otto claim the user "just" did something in a chat they threw away.
        clearDecisionLog();
        // ...and the per-session concept-note dedup: the note was shown in the chat just wiped, so a fresh
        // conversation may explain FCI (etc.) again.
        resetShownConceptNotes();
        planActiveRef.current = false;
        // Invalidate any turn still in flight — its continuation must not append the wiped thread's proposal.
        convoGenRef.current += 1;
    }, []);

    // Apply a filter card: forward to the dashboard (which merges it onto whatever is already applied and
    // pushes a pre-apply snapshot), then push THIS message (idx) onto the applied stack. Nothing else is
    // frozen — the user can keep applying filters from later questions, which accumulate on the dashboard.
    const applyFilters = useCallback(
        (idx, payload, trace) => {
            if (onApply) onApply(payload, trace);
            setAppliedStack(prev => (prev.includes(idx) ? prev : [...prev, idx]));
            // A plan paused on this filter step (or whose auto step just applied itself) continues now —
            // deferred a beat so the applied state lands before the next step runs.
            if (loadPlan() || planActiveRef.current) {
                setTimeout(() => runPlanLoopRef.current && runPlanLoopRef.current(), 350);
            }
        },
        [onApply]
    );

    // Undo the most-recently-applied filter: revert it on the dashboard (onUndo pops the dashboard's snapshot
    // stack and restores the pre-apply params) and pop it off our stack, so the previous applied card becomes
    // the undoable one. Only the top card exposes Undo, so this always targets that card.
    const undoFilters = useCallback(() => {
        if (onUndo) onUndo();
        setAppliedStack(prev => prev.slice(0, -1));
    }, [onUndo]);

    // The single project id Otto's turns were persisted under (mirrors buildProposal's interpretProjectId),
    // so the history list scopes to the SAME project the conversations were saved against.
    const historyProjectId = useMemo(
        () => String(projectId != null ? projectId : (projectIds && projectIds[0]) != null ? projectIds[0] : ""),
        [projectId, projectIds]
    );

    // Resume a past conversation from the history panel: load its turns, rebuild the chat from them, and
    // adopt its server session so new messages append to the same thread. Any in-flight queued query is
    // dropped — we're switching threads. The saveChat effect persists the swap like any other change.
    const openSession = useCallback(async session => {
        if (!session || !session.session_id) return;
        setShowHistory(false);
        // Switching threads invalidates everything scoped to the one on screen — the same set Clear wipes:
        // a pending plan, the question Otto was holding open, the decision log, and any turn still in
        // flight (the generation bump makes its continuation a no-op). Without this the resumed chat kept
        // answering the old thread's question and resuming its plan.
        convoGenRef.current += 1;
        clearPlan();
        clearQuestion();
        clearDecisionLog();
        planActiveRef.current = false;
        try {
            const data = await getSession(session.session_id);
            // Guard against a 200 that ISN'T a real session payload. The classic case: a reverse proxy whose
            // regex matches /sessions but not /sessions/{id} (a trailing `$` anchor), so the detail request
            // falls through to the SPA and returns index.html (HTTP 200 HTML). Without this check that would
            // silently load an EMPTY chat; surface it as an error so the misroute is visible, not swallowed.
            if (!data || typeof data !== "object" || !Array.isArray(data.turns)) {
                throw new Error("bad_session_payload");
            }
            setMessages(rehydrateMessages(data.turns));
            setSessionId(session.session_id);
            setPendingText(null);
            setAppliedStack([]);
            // Message indices now belong to a different conversation — drop the nav hops anchored to the old ones.
            clearNavBack();
            setNavBack(null);
        } catch (e) {
            setMessages([{ role: "assistant", error: "Couldn’t load that conversation." }]);
        }
    }, []);

    if (!enabled) return null;

    return (
        <div className="fai-root">
            {open && (
                <div
                    ref={panelRef}
                    className={`fai-panel ${dragging ? "is-dragging" : ""} ${resizing ? "is-resizing" : ""}`}
                    style={{
                        // Width covers the chat column plus the history sidebar when it's showing, so
                        // opening history widens the window instead of halving the conversation.
                        width: panelWidth(panelSize.w, showHistory),
                        height: panelHeight(panelSize.h),
                        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`
                    }}
                >
                    {RESIZE_HANDLES.map(h => (
                        <div
                            key={h.dir}
                            className={`fai-resize fai-resize-${h.dir}`}
                            role="separator"
                            aria-label={h.label}
                            onPointerDown={e => onResizePointerDown(e, h.dir)}
                            onPointerMove={onResizePointerMove}
                            onPointerUp={endResize}
                            onPointerCancel={endResize}
                        />
                    ))}
                    <div
                        className="fai-header"
                        onPointerDown={onHeaderPointerDown}
                        onPointerMove={onHeaderPointerMove}
                        onPointerUp={endHeaderDrag}
                        onPointerCancel={endHeaderDrag}
                    >
                        <span>
                            Otto <span className="fai-subtitle">· Dashboard AI Assistant</span>
                            {/* Which Otto is this? Without it, "is my fix deployed here?" is guesswork in
                                every environment — the same blind spot that makes an undeployed backend
                                look like a broken fix. `title` carries the backend's build too. */}
                            <span className="fai-version" title={`Otto ${OTTO_VERSION}`}>
                                v{OTTO_VERSION}
                            </span>
                        </span>
                        <div className="fai-header-actions">
                            {newsAvailable && (
                                <button
                                    className="fai-news-btn"
                                    type="button"
                                    onClick={reopenNews}
                                    aria-label="What's new in Otto"
                                    title="What's new in Otto"
                                >
                                    <NewsIcon />
                                    {newsUnseen && <span className="fai-news-dot" />}
                                </button>
                            )}
                            <button
                                className={`fai-size-toggle ${enlarged ? "is-on" : ""}`}
                                type="button"
                                onClick={toggleEnlarged}
                                aria-label={enlarged ? "Restore window size" : "Enlarge window"}
                                aria-pressed={enlarged}
                                title={enlarged ? "Restore window size" : "Enlarge window"}
                            >
                                {enlarged ? <ShrinkIcon /> : <ExpandIcon />}
                            </button>
                            <button
                                className={`fai-history-toggle ${showHistory ? "is-on" : ""}`}
                                type="button"
                                onClick={() => setShowHistory(v => !v)}
                                aria-label="Chat history"
                                aria-pressed={showHistory}
                                title="Chat history"
                            >
                                <HistoryIcon />
                            </button>
                            <button
                                className="fai-clear"
                                type="button"
                                onClick={clearConversation}
                                aria-label="Clear conversation"
                                title="Clear conversation"
                            >
                                <ClearIcon />
                            </button>
                            <button className="fai-close" onClick={() => setOpen(false)} aria-label="Minimize" title="Minimize">
                                <MinimizeIcon />
                            </button>
                        </div>
                    </div>
                    <div className="fai-body">
                        {showHistory && (
                            <HistoryPanel
                                userId={readUserId()}
                                projectId={historyProjectId}
                                activeSessionId={sessionId}
                                onSelect={openSession}
                            />
                        )}
                        <div className="fai-chat">
                            <div className="fai-messages" ref={listRef}>
                                {!schemaError && (
                                    <Row who="assistant">
                                        <div className="fai-msg fai-assistant fai-welcome">
                                            {WELCOME_MESSAGE}
                                            {/* Each chip is dropped when its list scopes down to nothing on
                                                this page, so a chip never opens an empty modal. */}
                                            <div className="fai-chips">
                                                {!!pageExamples.length && (
                                                    <button
                                                        type="button"
                                                        className="fai-chip"
                                                        onClick={() => openModal("examples")}
                                                    >
                                                        Examples
                                                    </button>
                                                )}
                                                {!!pageRecommendations.length && (
                                                    <button
                                                        type="button"
                                                        className="fai-chip"
                                                        onClick={() => openModal("recommendations")}
                                                    >
                                                        Recommendations
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </Row>
                                )}
                                {messages.map((m, i) => (
                                    <React.Fragment key={i}>
                                        <Message
                                            idx={i}
                                            message={m}
                                            maps={maps}
                                            appliedStack={appliedStack}
                                            undoableIdx={undoableIdx}
                                            onApply={onApply ? applyFilters : undefined}
                                            onUndo={undoFilters}
                                            onChartApply={onChartApply}
                                            onReset={onReset}
                                            onNavigate={onNavigate ? navigateTo : undefined}
                                            navHop={navBackEntry && navBackEntry.msgIdx === i ? navBackEntry : null}
                                            onTableApply={tableApply}
                                            onRunPlan={startPlan}
                                            onPageChoice={onPageChoice}
                                            onChoose={submitText}
                                            onClarify={onClarify}
                                            onQuestionAnswer={onQuestionAnswer}
                                            feasibilityCtx={feasibilityCtx}
                                            onOpenFilterWindow={onOpenFilterWindow}
                                        />
                                        {/* The back affordance sits directly under the card whose button/chip
                                            did the navigating. */}
                                        {navBackAnchored && navBackEntry.msgIdx === i && (
                                            <NavBackRow entry={navBackEntry} onNavBack={goBackToOrigin} />
                                        )}
                                    </React.Fragment>
                                ))}
                                {/* A hop with no card to anchor to (a plan's navigate step) — show it last. */}
                                {navBackEntry && !navBackAnchored && (
                                    <NavBackRow entry={navBackEntry} onNavBack={goBackToOrigin} />
                                )}
                                {pendingText && (
                                    <>
                                        <Row who="user">
                                            <div className="fai-msg fai-user">{pendingText}</div>
                                        </Row>
                                        <Row who="assistant">
                                            <div className="fai-msg fai-assistant">
                                                Loading your data... <DnaLoader />
                                            </div>
                                        </Row>
                                    </>
                                )}
                                {loading && (
                                    <Row who="assistant">
                                        <ThinkingBubble route={streamRoute} text={streamText} />
                                    </Row>
                                )}
                                {schemaError && (
                                    <div className="fai-error">Couldn’t reach the filter assistant service.</div>
                                )}
                            </div>
                            <div className="fai-input">
                                {/* Textarea + Send share ONE rounded shell so they read as a single control. */}
                                <div className="fai-composer">
                                    <textarea
                                        ref={inputRef}
                                        value={input}
                                        placeholder="Describe what you want to see…"
                                        onChange={e => setInput(e.target.value)}
                                        onKeyDown={onKeyDown}
                                        rows={1}
                                    />
                                    {/* Sits INSIDE the shell beside Send, where the user already is when they
                                        are about to type — the whole point is that it is seen before the
                                        first "what can I even ask?". Hidden when this mount can do nothing,
                                        which is the only case where the panel would have nothing to say. */}
                                    {!!pageActions.length && (
                                        <button
                                            type="button"
                                            className="fai-actions-btn"
                                            aria-label="What Otto can do on this page"
                                            title="What Otto can do on this page"
                                            onClick={() => openModal("actions")}
                                        >
                                            <ActionsIcon />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="fai-send"
                                        aria-label="Send"
                                        title="Send"
                                        onClick={submit}
                                        disabled={loading || !!pendingText || !input.trim() || !maps}
                                    >
                                        <SendIcon />
                                    </button>
                                </div>
                                <div className="fai-input-hint">Enter to send · Shift + Enter for a new line</div>
                            </div>
                        </div>
                    </div>
                    {activeModal === "actions" && (
                        <PromptModal title="What Otto can do here" onClose={() => setActiveModal(null)}>
                            {!!pageTitle && <div className="fai-actions-page">{pageTitle}</div>}
                            {pageActions.map(a => (
                                <div key={a.key} className="fai-action">
                                    <span className="fai-action-label">{a.label}</span>
                                    <span className="fai-action-blurb">{a.blurb}</span>
                                </div>
                            ))}
                            <div className="fai-actions-foot">
                                Just describe what you want in your own words — Otto shows you the change and
                                waits for you to confirm it.
                            </div>
                        </PromptModal>
                    )}
                    {activeModal === "examples" && (
                        <PromptModal title="Try one of these" onClose={() => setActiveModal(null)}>
                            {pageExamples.map(cat => {
                                const shown = exampleCounts[cat.title] || EXAMPLES_STEP;
                                return (
                                    <React.Fragment key={cat.title}>
                                        <div className="fai-examples-group">{cat.title}</div>
                                        {cat.prompts.slice(0, shown).map((q, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                className="fai-example"
                                                onClick={() => pickExample(q)}
                                            >
                                                {q}
                                            </button>
                                        ))}
                                        {shown < cat.prompts.length && (
                                            <button
                                                type="button"
                                                className="fai-load-more"
                                                onClick={() => loadMoreExamples(cat.title)}
                                            >
                                                Load more
                                            </button>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </PromptModal>
                    )}
                    {activeModal === "recommendations" && (
                        <PromptModal title="How to talk to Otto" onClose={() => setActiveModal(null)}>
                            {pageRecommendations.slice(0, exampleCounts[REC_GROUP] || REC_STEP).map((r, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className="fai-example fai-recommendation"
                                    onClick={() => pickExample(r.prompt)}
                                >
                                    <span className="fai-rec-tip">{r.tip}</span>
                                    <span className="fai-rec-prompt">{r.prompt}</span>
                                </button>
                            ))}
                            {(exampleCounts[REC_GROUP] || REC_STEP) < pageRecommendations.length && (
                                <button
                                    type="button"
                                    className="fai-load-more"
                                    onClick={() => loadMoreExamples(REC_GROUP, REC_STEP)}
                                >
                                    Load more
                                </button>
                            )}
                        </PromptModal>
                    )}
                </div>
            )}
            <button className="fai-launcher" onClick={openPanel} aria-label="Otto filter assistant">
                {open ? "×" : "Ask Otto"}
            </button>
            {!!newsDeck && (
                <React.Suspense fallback={null}>
                    <NewsModal slides={newsDeck} version={`v${OTTO_VERSION}`} onClose={closeNews} />
                </React.Suspense>
            )}
        </div>
    );
};

export default FilterAssistant;
